export const translations = {
  en: {
    language: {
      english: "English",
      khmer: "Khmer",
      switch: "Language",
    },
    loading: {
      pleaseWait: "Loading, please wait...",
    },
    auth: {
      logoutTitle: "Logged Out",
      logoutMessage: "You have been signed out successfully.",
    },
    registerPage: {
      brandTitle: "Everything your family needs, in one place.",
      brandDescription: "Create your account to save favorites, place orders, and follow every delivery.",
      benefits: {
        secureCheckout: "Secure checkout and account protection",
        liveTracking: "Live order and delivery tracking",
        fasterShopping: "Faster shopping with saved details",
      },
      title: "Create your account",
      subtitle: "Join Cherish Baby and start shopping in minutes.",
      verifyEmail: "Verify your email",
      verificationInstructions: "Enter the 6-digit code sent to",
      verificationCode: "Verification Code",
      verifying: "Verifying...",
      verify: "Verify Email",
      resendCode: "Resend Code",
      fullName: "Full Name",
      emailAddress: "Email Address",
      phoneNumber: "Phone Number",
      password: "Password",
      passwordPlaceholder: "Create a strong password",
      passwordHint: "Min. 6 characters",
      passwordChoiceTitle: "Choose how to create your password",
      strongPasswordRequirement: "Use at least 10 characters with uppercase, lowercase, a number, and a special character.",
      useSuggestedPassword: "Use suggested password",
      useOwnPassword: "Use my own password",
      passwordValidatorTitle: "Password Validator",
      passwordValidatorSubtitle: "Your password must meet all requirements below.",
      passwordStrength: {
        label: "Password strength",
        weak: "Weak",
        medium: "Medium",
        strong: "Strong",
      },
      passwordRules: {
        upperAndLower: "Lowercase & uppercase letters",
        number: "Number (0–9)",
        special: "Special character (!@#$%^&*)",
        length: "At least 10 characters",
      },
      agreePrefix: "I agree to the",
      termsOfService: "Terms of Service",
      and: "and",
      privacyPolicy: "Privacy Policy",
      creating: "Creating...",
      createAccount: "Create Account",
      alternativeSignUp: "Or sign up with",
      googleSignUp: "Sign up with Google",
      alreadyHaveAccount: "Already have an account?",
      signIn: "Sign in",
      secureRegistration: "Secure registration",
      googleConfirmTitle: "Continue with Google?",
      googleConfirmDescription: "Google will be verified on the server before account creation completes.",
      googleSelectedAccount: "Continue with your selected Google account",
      googleChooseAnother: "You can cancel and choose a different account if needed.",
      cancel: "Cancel",
      continue: "Continue",
      googleUserAlt: "Google user",
      errors: {
        requiredFields: "Please fill in all required fields: {{fields}}.",
        validEmail: "Please enter a valid email address.",
        gmailOnly: "Customer registration only accepts @gmail.com email addresses.",
        validCambodiaPhone: "Please enter a valid Cambodian mobile phone number.",
        passwordLength: "Password must contain at least 6 characters.",
        strongPassword: "Use a strong password with at least 10 characters, uppercase, lowercase, a number, and a special character.",
        acceptTerms: "Please agree to the Terms of Service and Privacy Policy.",
        registrationFailed: "Registration failed. Please try again.",
        duplicateEmail: "This email is already registered. Please use a different email or sign in.",
        verificationCodeRequired: "Please enter the verification code from your email.",
        verificationFailed: "Could not verify this code. Please try again.",
        resendFailed: "Could not resend verification code.",
        googleFailed: "Google sign up failed. Please try again.",
        googleMissingToken: "Google sign up response is missing an authentication token.",
        wrongAccountType: "Not a customer account. Please use appropriate credentials.",
      },
      messages: {
        verificationSent: "We sent a verification code to your email.",
        verificationSuccess: "Email verified successfully. You can now sign in.",
        verificationResent: "A new verification code has been sent.",
      },
      fieldNames: {
        fullName: "full name",
        emailAddress: "email address",
        phoneNumber: "phone number",
        password: "password",
      },
    },
    reviewOrder: {
      loading: "Loading review page...",
      deliveredOrder: "Delivered order",
      title: "Rate your products",
      subtitle: "Share a quick rating for the products from order #{{orderNumber}}.",
      purchasedProduct: "Purchased product",
      quantity: "Quantity {{count}}",
      rating: "Rating",
      comment: "Comment",
      commentPlaceholder: "Share what you liked, how the product worked, or anything other customers should know.",
      commentHint: "Optional, but helpful for other families.",
      readyTitle: "Ready to submit all reviews?",
      submitHint: "Add a rating for all {{count}} product(s) before submitting.",
      submittingAll: "Submitting all...",
      submitAll: "Submit all reviews",
      signInOrderAccount: "Sign in with order account",
      backToOrders: "Back to orders",
      status: {
        submitted: "Submitted",
        updated: "Review updated",
        existing: "Already reviewed",
      },
      notices: {
        alreadyRated: "You already rated this product. You can change the stars, then update your review.",
        submitted: "Review submitted for this product. You can continue with the next product, or update this one.",
      },
      success: {
        badge: "Review submitted",
        title: "Thank you for your review",
        message: "Your feedback is live and helps other families choose with confidence.",
        productReviewed: "Product reviewed",
        productsReviewed: "Products reviewed",
        autoReturn: "Auto return",
        returningHome: "Returning to home",
        almostThere: "Almost there",
        continueShopping: "Continue shopping",
        viewOrders: "View orders",
      },
      errors: {
        loadOrder: "Unable to load this order.",
        chooseRating: "Please choose a rating.",
        selectEveryRating: "Please select a star rating for every product before submitting.",
        submitFailed: "Failed to submit review.",
        pageUnavailable: "Review page unavailable",
        wrongAccount: "This order belongs to a different customer account. Please sign in with the account that placed this order, then open the review link again.",
        orderNotFound: "Order not found.",
      },
    },
    nav: {
      home: "Home",
      wishlist: "Wishlist",
      cart: "Cart",
      profile: "Profile",
      orders: "Orders",
      myOrders: "My Orders",
      settings: "Settings",
      logout: "Logout",
      login: "Login",
      register: "Register",
      browse: "Browse",
      account: "Account",
      more: "More",
      about: "About Us",
      contact: "Contact",
      location: "Store Location",
      welcome: "Welcome!",
      light: "Light",
      dark: "Dark",
      lightMode: "Light mode",
      darkMode: "Dark mode",
      loginRegister: "Login / Register",
      switchToLight: "Switch to light mode",
      switchToDark: "Switch to dark mode",
    },
    product: {
      add: "Add",
      addedTitle: "Product Added",
      addedSuccess: "Product added successfully.",
      addFailedTitle: "Add Failed",
      addFailed: "Failed to add product",
      all: "All",
      bestSeller: "Best Seller",
      browseFullCollection: "Browse the full collection in one place.",
      deal: "Deal",
      details: "Details",
      essentials: "Essentials",
      expires: "Expires",
      expiryDate: "Expiry Date",
      exploreEverything: "Explore Everything",
      freshPicks: "Fresh picks recently added to the collection.",
      homeCollection: "Home Collection",
      items: "Items",
      newArrival: "New Arrival",
      noProductsFound: "No products found",
      noProductsMessage: "We couldn't find products for the current search or category filter.",
      noResultsFound: "No results found",
      noResultsMessage: "We couldn't find any products matching your current filters.",
      productCatalog: "Product Catalog",
      premiumQuality: "Premium quality for your lifestyle.",
      recommendedForYou: "Recommended For You",
      recommendedFromPurchases: "Based on products you purchased before.",
      recommendedFromViews: "Based on products you viewed recently.",
      searchPlaceholder: "Search products...",
      seeMore: "See More",
      showMoreCategories: "Show more categories",
      showingMatches: "Showing everything that matches \"{{query}}\".",
      showingViewMatches: "Showing {{view}} matching \"{{query}}\".",
      soldOut: "Sold Out",
      save: "Save",
      selectSize: "Select Size",
      selectSizeMessage: "Please select a size before adding this product to your cart.",
      chooseSize: "Choose Size",
      strongestSavings: "Current discounted items with the strongest savings.",
      popularProducts: "Popular products ranked by demand and ratings.",
      quickAdd: "Quick Add",
      updatedTitle: "Product Updated",
      updatedSuccess: "Product updated successfully.",
      updateFailedTitle: "Update Failed",
      updateFailed: "Update failed",
      basedOnReviews: "Based on {{count}} Reviews",
      reviewCount: "{{count}} Reviews",
      pleaseLoginToReview: "Please log in to write a review",
      submitReview: "Submit Review",
      submittingReview: "Submitting...",
      excellent: "Excellent",
      veryGood: "Very Good",
      good: "Good",
      poor: "Poor",
      bad: "Bad",
      customerReviews: "Customer Reviews",
      viewCustomerReviews: "View customer reviews",
      backToProduct: "Back to product",
      reviewsForProduct: "Reviews for {{product}}",
      verifiedReviews: "Verified Reviews",
      verifiedCustomerFeedback: "Verified Customer Feedback",
      deliveredReviewDisplay: "Ratings and comments from delivered purchases appear here.",
      showMoreReviews: "Show {{count}} more reviews",
      showFewerReviews: "Show fewer reviews",
      noReviewsYet: "No Reviews Yet",
      firstReview: "Be the first to review this product.",
      writeReview: "Write a Review",
      reviewSubmitted: "Review Submitted",
      feedbackThanks: "Thank you for your feedback!",
      rating: "Rating",
      selectRating: "Select rating",
      yourReview: "Your Review",
      reviewPlaceholder: "Write your review here...",
      login: "Log In",
      youMightAlsoLike: "You Might Also Like",
      loadingProducts: "Loading products...",
      noImportChargesCheckout: "No import charges. Delivery calculated at checkout.",
      fastLocalDeliveryCambodia: "Fast local delivery available in Cambodia",
      deliverSavedAddressCheckout: "Deliver to your saved address at checkout",
      secureCheckoutProtectedPayment: "Secure checkout with protected payment",
      inStock: "In Stock",
      outOfStock: "Out of Stock",
      out: "Out",
      size: "Size",
      color: "Color",
      quantity: "Quantity",
      decreaseQuantity: "Decrease quantity",
      increaseQuantity: "Increase quantity",
      availableCount: "{{count}} available",
      qtyCount: "qty {{count}}",
      chooseColor: "Choose a color",
      chooseColorMessage: "Please choose a product color before adding it to your cart.",
      addToCart: "Add to Cart",
      loginToBuy: "Log in to Buy",
      stockLimitReached: "Stock limit reached",
      ok: "OK",
    },
    footer: {
      brandDescription: "Your ultimate destination for premium products and an exceptional shopping experience. Quality guaranteed, satisfaction delivered.",
      shop: "Shop",
      allProducts: "All Products",
      newArrivals: "New Arrivals",
      bestSellers: "Best Sellers",
      deals: "Deals",
      support: "Support",
      contact: "Contact",
      faq: "FAQ",
      trackOrder: "Track Order",
      company: "Company",
      aboutUs: "About Us",
      location: "Location",
      privacyPolicy: "Privacy Policy",
      termsOfService: "Terms of Service",
      rightsReserved: "All rights reserved.",
      secured: "Secured",
    },
    contact: {
      title: "Contact Support",
      subtitle: "Get in touch with our team",
      badge: "Contact Us",
      phone: "Phone",
      email: "Email",
      telegram: "Telegram",
      voiceSupport: "Voice Support",
      emailTerminal: "Email Terminal",
      liveProtocol: "Live Protocol",
      phoneHours: "Mon-Fri from 8am to 5pm",
      emailHours: "24/7 Response coverage",
      directChat: "Direct Chat",
      instantAgents: "Instant uplink with agents",
      sendMessage: "Send Message",
      formIntro: "Have a specific inquiry? Fill out the form below to send detailed information directly to our team.",
      fullName: "Full Name",
      emailAddress: "Email Address",
      inquiryTopic: "Inquiry Topic",
      message: "Message",
      enterName: "Enter name...",
      enterEmail: "Enter email...",
      yourMessage: "Your message...",
      technicalSupport: "Technical Support",
      billingFinance: "Billing & Finance",
      partnership: "Partnership Proposal",
      otherGeneral: "Other / General",
      businessHours: "Business Hours",
      mondaySunday: "Monday - Sunday",
      mondayFriday: "Monday - Friday",
      saturdaySunday: "Saturday - Sunday",
      publicHolidays: "Public Holidays",
      closed: "Closed",
      sending: "Sending...",
      sendSuccessTitle: "Message Sent",
      sendSuccessMessage: "Your support message was sent by email successfully.",
      ticketSavedMessage: "Your support request was saved. Email delivery is not configured or failed.",
      sendFailedTitle: "Send Failed",
      sendFailedMessage: "Could not send your message. Please try again.",
      requiredFields: "Please enter your name, email, and message.",
    },
    completeProfile: {
      title: "Complete Profile",
      subtitle: "Add your phone number to continue",
      prompt: "Please add your phone number before continuing.",
      phoneNumber: "Phone Number",
      phoneRequired: "Please enter your phone number.",
      invalidPhone: "Please enter a valid Cambodia phone number.",
      phoneInUse: "This phone number is already used by another account.",
      saving: "Saving...",
      savePhone: "Save Phone Number",
      logout: "Logout and Try Later",
      successTitle: "Profile Completed",
      savedSuccess: "Phone number saved successfully.",
      saveFailed: "Failed to save phone number.",
      success: "Success!",
      redirecting: "Redirecting you to the home page...",
    },
    about: {
      title: "About Us",
      subtitle: "Redefining the online shopping experience since 2019",
      badge: "Our Story",
      mission: "Our Mission",
      curatedQuality: "Curated Quality",
      missionText: "To revolutionize your online shopping experience by curating exceptional products that enhance your lifestyle. We're committed to making premium quality accessible while maintaining the highest standards of customer service and satisfaction.",
      vision: "Our Vision",
      modernEssentials: "Modern Essentials",
      visionText: "To become the world's most trusted online marketplace where quality meets convenience. We envision a future where every customer can shop with confidence, knowing they're getting the best products at fair prices with exceptional service.",
      customerFirst: "Customer First",
      customerFirstSub: "Our top priority",
      globalReach: "Global Reach",
      globalReachSub: "Delivering everywhere",
      yearsOfGrowth: "Years of Growth",
      growthText: "Building trust and delivering quality to thousands of customers across Cambodia.",
      advantages: "Our Advantages",
      whyChooseUs: "Why Choose Us",
      journey: "Our Journey",
      milestonesTitle: "Milestones",
      leadership: "Leadership",
      teamTitle: "Our Team",
      stats: {
        dailyProducts: "Daily Products",
        valueDelivered: "Value Delivered",
        happyCustomers: "Happy Customers",
        trustedBrands: "Trusted Brands",
      },
      values: {
        secureTechnology: {
          title: "Secure Technology",
          description: "Your security is our priority. We use state-of-the-art encryption to ensure your data and transactions are always safe.",
        },
        qualityAssurance: {
          title: "Quality Assurance",
          description: "We handpick every item in our collection. Only the best products make it to our store shelves with certified verification.",
        },
        customerSupport: {
          title: "Customer Support",
          description: "Our dedicated support team is here to help you around the clock. Your satisfaction is 100% guaranteed.",
        },
      },
      team: {
        ceoFounder: "CEO & Founder",
        headOfOperations: "Head of Operations",
        techLead: "Tech Lead",
        marketingDirector: "Marketing Director",
      },
      milestones: {
        founded: {
          label: "Founded",
          detail: "Launched with a vision to transform online shopping",
        },
        customers10k: {
          label: "10K Customers",
          detail: "Reached 10,000 happy customers across Cambodia",
        },
        regionalExpansion: {
          label: "Regional Expansion",
          detail: "Expanded delivery network to 5 provinces",
        },
        milestone50k: {
          label: "50K Milestone",
          detail: "Served 50,000 customers with 100+ brand partners",
        },
      },
    },
    empty: {
      noItemsFound: "No items found",
      browseProducts: "Browse Products",
    },
    cart: {
      shoppingCart: "Shopping Cart",
      itemCount: "You have {{count}} {{itemLabel}}",
      item: "item",
      items: "items",
      emptyTitle: "Your cart is empty",
      emptyMessage: "Looks like you haven't added anything to your cart yet.",
      emptyHint: "Start shopping to find the best essentials for your baby.",
      startShopping: "Start Shopping",
      orderSummary: "Order Summary",
      subtotal: "Subtotal",
      shipping: "Shipping",
      free: "Free",
      tax: "Tax (8.0%)",
      total: "Total",
      inStock: "In Stock",
      sizeValue: "Size {{size}}",
      decrease: "Decrease",
      increase: "Increase",
      removeItem: "Remove Item",
      removeAllItems: "Remove all",
      confirmRemoveAllMessage: "Are you sure you want to remove all items?",
      proceedToCheckout: "Proceed to Checkout",
      securePayment: "Secure Payment",
      previewTitle: "Cart",
      drawerItemCount: "{{count}} items",
      closePreview: "Close cart preview",
      openWishlist: "Open wishlist",
      drawerEmptyMessage: "Add a product and it will appear here.",
      cartProduct: "Cart product",
      calculatedAtCheckout: "Calculated at checkout",
      checkout: "Checkout",
      discountOff: "{{percent}}% off",
      loginRequiredTitle: "Login Required",
      loginRequiredMessage: "Please login to add items to your cart",
      addedTitle: "Added to Cart",
      addedMessage: "This item{{size}} has been added to your cart.",
      stockLimitMessage: "This item only has {{available}} left, but {{requested}} were requested. Please update your cart and try again.",
      actionFailedTitle: "Action Failed",
      addFailedMessage: "Could not add item to cart. Please try again.",
      updateFailedTitle: "Update Failed",
      updateFailedMessage: "Could not update item quantity.",
      removedTitle: "Item Removed",
      removedMessage: "Item has been removed from your cart.",
      removeFailedTitle: "Remove Failed",
      removeFailedMessage: "Could not remove item from cart.",
      clearFailedTitle: "Clear Failed",
      clearFailedMessage: "Could not clear cart.",
    },
    orderDetail: {
      loading: "Loading order...",
      notFound: "Order Not Found",
      fetchFailed: "Something went wrong while loading order details.",
      notAuthenticated: "Please sign in to view this order.",
      back: "Back",
      backToOrders: "Back to orders",
      backToHome: "Back to home",
      orderNumber: "Order #{{number}}",
      items: "Items",
      qty: "Qty",
      size: "Size",
      color: "Color",
      shipping: "Shipping",
      payment: "Payment",
      method: "Method",
      paymentStatus: "Payment",
      paidOn: "Paid on",
      summary: "Summary",
      subtotal: "Subtotal",
      free: "Free",
      tax: "Tax",
      total: "Total",
      undefined: "Undefined",
      cancelOrder: "Cancel Order",
      cancelling: "Cancelling...",
      cancelConfirmTitle: "Cancel this order?",
      cancelConfirm: "Cancel this order? You can only cancel before the seller confirms it.",
      keepOrder: "Keep Order",
      confirmCancel: "Yes, Cancel Order",
      cancelFailed: "Failed to cancel order",
      cancelledNotice: "This order has been cancelled.",
      status: {
        pending: "Pending",
        processing: "Processing",
        shipped: "Shipped",
        delivered: "Delivered",
        cancelled: "Cancelled",
        paid: "Paid",
        failed: "Failed",
        refunded: "Refunded",
      },
      paymentMethods: {
        cashondelivery: "Cash on Delivery",
        bakongkhqr: "Bakong KHQR",
        undefined: "Undefined",
      },
    },
    orderTracking: {
      title: "Track Order",
      subtitle: "Enter your order number to check the current status and delivery progress",
      badge: "Tracking",
      placeholder: "Enter full order ID or last 8 characters",
      track: "Track",
      tracking: "Tracking...",
      order: "Order",
      status: "Status",
      estimatedDelivery: "Estimated Delivery",
      deliveredOn: "Delivered On",
      trackingId: "Tracking ID",
      storeDelivery: "Store delivery",
      trackingHistory: "Tracking History",
      pending: "Pending",
      processed: "Processed",
      destination: "Destination",
      recipient: "Recipient",
      deliveryAddress: "Delivery address",
      customer: "Customer",
      timeline: {
        orderPlaced: "Order Placed",
        orderProcessed: "Order Processed",
        shipped: "Shipped",
        delivered: "Delivered",
      },
      locations: {
        onlineCheckout: "Online checkout",
        sellerConfirmation: "Seller confirmation",
        readyForDelivery: "Ready for delivery",
      },
      errors: {
        enterOrderNumber: "Please enter an order number.",
        notFound: "Order not found.",
        notAuthorized: "You are not authorized to track this order.",
        trackFailed: "Unable to track this order. Please check the order number and try again.",
      },
    },
    ordersPage: {
      loading: "Syncing history...",
      identityNeeded: "Identity Needed",
      identityMessage: "Please enter your credentials to view history.",
      enterVault: "Enter Vault",
      goBack: "Go back",
      myOrders: "My Orders",
      orderHistory: "Order History",
      historySubtitle: "View details of your past orders",
      ordersPlaced: "{{count}} orders placed",
      noOrdersFound: "No Orders Found",
      noOrdersMessage: "You haven't placed any orders yet. Start shopping to find the best essentials for your baby.",
      startShopping: "Start Shopping",
      itemsCount: "Items ({{count}})",
      shippingTo: "Shipping to",
      mapLocationSelected: "Map location selected",
      viewDetails: "View Details",
      errors: {
        notAuthenticated: "Not authenticated",
        fetchFailed: "Failed to fetch orders",
      },
    },
    profile: {
      accessDenied: "Access Denied",
      accessDeniedMessage: "Please log in to view your account overview and manage your profile.",
      signInNow: "Sign In Now",
      change: "Change",
      success: "Success",
      error: "Error",
      overview: "Overview",
      profileDetails: "Profile Details",
      securityLogin: "Security & Login",
      totalOrders: "Total Orders",
      wishlistItems: "Wishlist Items",
      itemsInCart: "Items in Cart",
      viewAll: "View All",
      accountSummary: "Account Summary",
      edit: "Edit",
      fullName: "Full Name",
      email: "Email",
      phone: "Phone",
      joined: "Joined",
      notProvided: "Not provided",
      recentActivity: "Recent Activity",
      noOrdersYet: "No orders yet",
      noOrdersMessage: "When you make a purchase, it will appear here.",
      startShopping: "Start Shopping",
      updateProfile: "Update Profile",
      updateProfileDescription: "Manage your personal information and contact details.",
      editProfile: "Edit Profile",
      cancel: "Cancel",
      emailAddress: "Email Address",
      phoneNumber: "Phone Number",
      saving: "Saving...",
      saveChanges: "Save Changes",
      securitySettings: "Security Settings",
      securityDescription: "Update your password to keep your account secure.",
      currentPassword: "Current Password",
      newPassword: "New Password",
      confirmNewPassword: "Confirm New Password",
      enterCurrentPassword: "Enter current password",
      enterNewPassword: "Enter new password",
      confirmNewPasswordPlaceholder: "Confirm new password",
      updatingSecurity: "Updating Security...",
      updatePassword: "Update Password",
      emailStep: "Email",
      verifyStep: "Verify",
      passwordStep: "Password",
      enterAccountEmail: "Enter your account email",
      sendingCode: "Sending Code...",
      sendVerificationCode: "Send Verification Code",
      verificationCode: "Verification Code",
      codeSentTo: "We sent a 6-digit code to {{email}}.",
      verifying: "Verifying...",
      verifyCode: "Verify Code",
      resendIn: "Resend in {{seconds}}s",
      resendCode: "Resend Code",
      startOver: "Start Over",
      enterEmailRequired: "Please enter your email address.",
      accountEmailRequired: "Please enter the email address for this account.",
      verificationCodeSent: "Verification code sent to your email.",
      sendVerificationFailed: "Failed to send verification code.",
      enterSixDigitCode: "Please enter the 6-digit verification code.",
      codeVerified: "Code verified. Enter your new password.",
      verifyCodeFailed: "Failed to verify code.",
      newVerificationCodeSent: "A new verification code was sent.",
      resendCodeFailed: "Failed to resend verification code.",
      verifyEmailFirst: "Please verify your email code first.",
      passwordUpdated: "Password updated successfully.",
      passwordUpdateFailed: "Failed to update password.",
      avatarTooLarge: "Avatar image must be less than 5MB",
      notAuthenticated: "Not authenticated",
      currentPasswordRequired: "Current password is required to change password",
      passwordsDoNotMatch: "New passwords do not match",
      passwordTooShort: "New password must be at least 6 characters",
      strongPasswordRequired: "Use at least 10 characters with uppercase, lowercase, a number, and a special character.",
      profileUpdated: "Profile updated successfully!",
      updateFailed: "Failed to update profile",
      myProfile: "My Profile",
      myOrders: "My Orders",
      wishlist: "Wishlist",
      settings: "Settings",
      signOut: "Sign Out",
      administrator: "Administrator",
      valuedCustomer: "Valued Customer",
    },
    mapPicker: {
      selectLocationOnMap: "Select Location on Map",
      updateLocationOnMap: "Update location",
      selectLocation: "Select Location",
      loadingMap: "Loading Map...",
      pleaseWait: "Please wait a moment",
      searchPlaceholder: "Search location...",
      useCurrentLocation: "Use current location",
      currentLocation: "Current Location",
      deliveryLocation: "Delivery Location",
      mapHint: "Tap the map or drag the marker",
      cancel: "Cancel",
      saving: "Saving...",
      confirmLocation: "Confirm Location",
      cambodiaOnly: "Please choose a location inside Cambodia only.",
      mapInitFailed: "Failed to initialize map. Please try again.",
      googleMapsMissingKey: "Google Maps API key is missing. Set VITE_GOOGLE_MAPS_API_KEY in your environment.",
      googleMapsAuthFailed: "Google Maps is not authorized for this site. Enable billing and allow {{origin}}/* in your Google Maps API key settings.",
      googleMapsLoadFailed: "Failed to load Google Maps. Please check your API key and try again.",
      searchNotFound: "Location not found. Try a more specific place.",
      geolocationUnsupported: "Geolocation is not supported by your browser.",
      detectionFailed: "Could not detect your location.",
      locationDenied: "Location access denied. Allow location for this site in your browser settings.",
      locationUnavailable: "Current location is unavailable. Turn on device location services and try again.",
    },
    checkout: {
      shippingAddress: "Shipping Address",
      fullName: "Full Name",
      enterFullName: "Enter your full name",
      pinLocation: "Pin Location",
      locationSelected: "Location Selected",
      optional: "Optional",
      street: "Street",
      streetPlaceholder: "Enter street name or number",
      address: "Address",
      addressPlaceholder: "Enter address details",
      selectLocationOnMap: "Select a location on the map",
      cityProvince: "City / Province",
      cityProvincePlaceholder: "Enter city or province",
      phoneNumber: "Phone Number",
      orderSuccessTitle: "Order Placed Successfully!",
      orderSuccessMessage: "Thank you for your order. We are preparing your items for shipment.",
      orderId: "Order ID",
      orderCreated: "Order Created",
      cancelNoticeTitle: "Need to cancel?",
      cancelNoticeMessage: "You can cancel this order from My Orders until the seller confirms it.",
      viewOrderDetails: "View Order Details",
      continueShopping: "Continue Shopping",
      bakongQrNotice: "Select Bakong KHQR, then click Place Order & Generate QR. A secure QR with the exact payment amount will appear.",
      bakongCancellationWarning: "Important: after a Bakong KHQR payment is completed, this order cannot be cancelled.",
      bakongWarningTitle: "Please confirm before paying",
      bakongWarningDetail: "You may leave or cancel before payment is completed. After Bakong confirms the payment, the order becomes final and cannot be cancelled.",
      chooseAnotherMethod: "Choose another method",
      understandContinue: "I understand, continue",
      bakongWarningClose: "Close payment warning",
      generatingQr: "Generating QR...",
      processing: "Processing...",
      placeOrderGenerateQr: "Place Order & Generate QR",
      placeOrder: "Place Order",
      errors: {
        incompleteShipping: "Please complete your shipping details before placing the order.",
        missingLocation: "Please select your delivery location on the map before placing the order.",
        emptyCart: "Your cart is empty. Add an item before placing the order.",
      },
    },
    bakongPayment: {
      generatingSecureQr: "Generating your secure QR code…",
      paymentError: "Payment Error",
      tryAgain: "Try Again",
      backToOrder: "Back to Order",
      backToCheckout: "Back to Checkout",
      paymentSuccessful: "Payment Successful!",
      paymentConfirmed: "Your payment has been confirmed. Redirecting to your order…",
      redirecting: "Redirecting in a few seconds",
      paymentFailed: "Payment Failed",
      paymentFailedMessage: "Something went wrong. Please try again or choose a different payment method.",
      qrExpired: "QR Code Expired",
      qrExpiredMessage: "Your QR code has expired after 5 minutes. Generate a new one to complete your payment.",
      generating: "Generating…",
      newQrCode: "New QR Code",
      back: "Back",
      scanToPay: "Scan to pay",
      waitingForPayment: "Waiting for payment",
      live: "Live",
      expiresIn: "Expires in",
      scanInstruction: "Scan with Bakong or any KHQR-supported banking app",
      amountDue: "Amount due",
      currency: "Currency",
      usd: "US Dollar",
      khr: "Khmer Riel",
      merchant: "Merchant",
      order: "Order",
      status: "Status",
      checkingAutomatically: "Checking automatically",
      paymentStatusUpdates: "Payment status updates automatically",
      payWithBankingApp: "Pay with banking app",
      saveQr: "Save or share QR",
      shareQrTitle: "Cherish Baby Store KHQR",
      shareQrText: "Save this KHQR, then import it from your banking app's QR scanner.",
      qrSavedTitle: "QR saved",
      qrSavedMessage: "Open your banking app and import the QR from Photos or Gallery.",
      qrSaveFailedTitle: "Could not save QR",
      qrSaveFailedMessage: "Press and hold the QR image, then choose Save to Photos.",
      samePhoneHelp: "Paying on this phone?",
      samePhoneStep1: "Tap “Save or share QR” and save the image.",
      samePhoneStep2: "Open ABA, ACLEDA, or another KHQR banking app.",
      samePhoneStep3: "Open its QR scanner, import from Photos/Gallery, then confirm payment.",
      refresh: "Refresh",
      cancelling: "Cancelling…",
      cancelPayment: "Cancel payment",
      securityNote: "Secured by NBC Bakong · Accepted by all KHQR-enabled banks",
      qrAlt: "Bakong KHQR payment code",
    },
    wishlistAlerts: {
      loginRequired: "Login Required",
      loginToAdd: "Please login to add items to your wishlist",
      loginToManage: "Please login to manage your wishlist",
      thisProduct: "This product",
      savedTitle: "Saved to Wishlist",
      savedMessage: "{{product}} has been saved.",
      removedTitle: "Removed from Wishlist",
      removedMessage: "Item has been removed from your wishlist.",
      actionFailed: "Action Failed",
      addFailed: "Could not add item to wishlist.",
      removeFailedTitle: "Remove Failed",
      removeFailed: "Could not remove item from wishlist.",
    },
    wishlistGuest: {
      privateList: "Your private wishlist",
      title: "Log in or register to view your wishlist",
      description: "Create an account or log in to save your favorite products and find them again on any device.",
      login: "Log In",
      register: "Register",
      accountNote: "Your saved products are kept securely with your account.",
    },
    location: {
      title: "Global Presence",
      subtitle: "Physical infrastructure and strategic hubs",
      badge: "Node Locations",
      globalHeadquarters: "Global Headquarters",
      europeanHub: "European Logistics Hub",
      operational: "Operational",
      standby: "Standby",
      syncStatus: "Sync Status",
      globalNetworkActive: "Global Network Active",
      activeHubs: "Active Hubs: 02 // Node Points: 48",
      primaryEntities: "Primary Entities",
      requestDirections: "Request Directions",
      worldwideFulfillment: "Worldwide Fulfillment",
      fulfillmentText: "Our logistics network spans across 6 continents, utilizing 150+ distribution nodes to ensure your acquisitions reach you via the most efficient vectors possible.",
      uplinkDirectly: "Uplink Directly",
    },
    help: {
      title: "Help Center",
      subtitle: "Find answers, guides, and direct support faster",
      badge: "Support",
      customerSupportHub: "Customer support hub",
      howCanWeHelp: "How can we help you?",
      intro: "Search by topic or question to find the right FAQ faster.",
      searchPlaceholder: "Search topics or questions...",
      tracking: "Tracking",
      warranty: "Warranty",
      deliveryTime: "Delivery time",
      security: "Security",
      orderChanges: "Order changes",
      coverage: "Coverage",
      selfService: "Self-service support",
      topics: "Topics",
      commonIssues: "Covering common issues",
      needPerson: "Need a person?",
      contactSupport: "Contact support",
      escalateIssue: "Escalate your issue",
      browseByTopic: "Browse By Topic",
      faq: "Frequently Asked Questions",
      searchResults: "Search results",
      questionsMatch: "Questions match your current search.",
      supportRoute: "Support route",
      liveAssistance: "Live assistance",
      moveToContact: "Move to contact if needed.",
      category: "Category",
      topic: "Topic",
      articles: "articles",
      article: "article",
      questions: "questions",
      question: "question",
      noMatches: "No matches",
      noArticles: "No articles matched that search.",
      noArticlesHint: "Try a broader keyword like tracking, warranty, delivery, or account. If the issue is specific, contact support directly instead of guessing.",
      noQuestions: "No questions matched that search.",
      noQuestionsHint: "Try a broader keyword like tracking, warranty, delivery, orders, or payment. If the issue is specific, contact support directly.",
      escalationPath: "Escalation path",
      stillNeedHelp: "Still need help with your order, account, or warranty support?",
      escalationText: "Use the contact page for issues that need account access, manual order review, or a billing investigation.",
      ordersProducts: "Orders & Products",
      ordersProductsDescription: "Order changes, stock questions, sizing help, and product care.",
      orderEdits: "Order edits",
      itemAvailability: "Item availability",
      productQuality: "Product quality",
      shippingDelivery: "Shipping & Delivery",
      shippingDeliveryDescription: "Tracking timelines, delivery methods, delays, and missed drop-offs.",
      trackPackage: "Track package",
      deliveryWindow: "Delivery window",
      shippingOptions: "Shipping options",
      paymentsBilling: "Payments & Billing",
      paymentsBillingDescription: "Accepted payment methods, invoice issues, and payment confirmation.",
      billingIssues: "Billing issues",
      warrantySupport: "Warranty support",
      paymentMethods: "Payment methods",
      securityPrivacy: "Security & Privacy",
      securityPrivacyDescription: "Account protection, privacy controls, and verification flows.",
      accountSecurity: "Account security",
      privacyRequests: "Privacy requests",
      verification: "Verification",
      trackOrderQuestion: "How do I track my order?",
      trackOrderAnswer: "Once your order is processed, a tracking number is sent to your registered email. You can use that link to follow every delivery update.",
      deliveryTimeQuestion: "What is the standard delivery time?",
      deliveryTimeAnswer: "Standard delivery usually takes 3 to 5 business days. Express delivery options are available during checkout when supported in your area.",
      packageDelayedQuestion: "What happens if my package is delayed?",
      packageDelayedAnswer: "If the carrier reports a delay, the latest status will still appear in your tracking link. If there is no movement for an extended period, contact support with your order number.",
      modifyOrderQuestion: "Can I modify my order after placing it?",
      modifyOrderAnswer: "Changes are allowed within 2 hours of checkout. After that, the order usually enters fulfillment and can no longer be edited.",
      qualityQuestion: "How are product quality standards verified?",
      qualityAnswer: "Each item is checked before dispatch to confirm packaging, condition, and basic quality standards so you receive the correct product in good shape.",
      cancelQuestion: "Can I cancel an item before it ships?",
      cancelAnswer: "If the order has not entered packing or carrier handoff, support can usually help with cancellation. Requests submitted earlier are more likely to be approved.",
      warrantyQuestion: "How do I request warranty support?",
      warrantyAnswer: "Select your delivered order on the contact page. If the product has warranty coverage, support will review it within that warranty period. Products without warranty use a 7-day support window after delivery.",
      declinedQuestion: "Why was my payment declined?",
      declinedAnswer: "Declines can happen because of card limits, verification mismatches, or bank-side fraud checks. Confirm your billing details and try again or use another payment method.",
    },
    categories: {
      all: "All",
      milk: "Milk",
      clothing: "Clothing",
      shoes: "Shoes",
      feedingNursing: "Feeding & Nursing",
      diaperingCare: "Diapering & Care",
      furniture: "Furniture",
      travelGear: "Travel & Gear",
      bathSkin: "Bath & Skin",
      toyPlay: "Toy & Play",
    },
  },
  kh: {
    language: {
      english: "English",
      khmer: "ខ្មែរ",
      switch: "ភាសា",
    },
    loading: {
      pleaseWait: "កំពុងផ្ទុក សូមរង់ចាំ...",
    },
    auth: {
      logoutTitle: "បានចេញពីគណនី",
      logoutMessage: "អ្នកបានចេញពីគណនីដោយជោគជ័យ។",
    },
    registerPage: {
      brandTitle: "អ្វីគ្រប់យ៉ាងដែលគ្រួសាររបស់អ្នកត្រូវការ នៅកន្លែងតែមួយ។",
      brandDescription: "បង្កើតគណនីដើម្បីរក្សាទុកផលិតផលដែលចូលចិត្ត បញ្ជាទិញ និងតាមដានការដឹកជញ្ជូន។",
      benefits: {
        secureCheckout: "ការទូទាត់ និងគណនីមានសុវត្ថិភាព",
        liveTracking: "តាមដានការបញ្ជាទិញ និងការដឹកជញ្ជូនផ្ទាល់",
        fasterShopping: "ទិញទំនិញលឿនជាងមុនដោយរក្សាទុកព័ត៌មាន",
      },
      title: "បង្កើតគណនីរបស់អ្នក",
      subtitle: "ចូលរួមជាមួយ Cherish Baby និងចាប់ផ្តើមទិញទំនិញក្នុងរយៈពេលប៉ុន្មាននាទី។",
      verifyEmail: "ផ្ទៀងផ្ទាត់អ៊ីមែលរបស់អ្នក",
      verificationInstructions: "បញ្ចូលលេខកូដ 6 ខ្ទង់ដែលបានផ្ញើទៅ",
      verificationCode: "លេខកូដផ្ទៀងផ្ទាត់",
      verifying: "កំពុងផ្ទៀងផ្ទាត់...",
      verify: "ផ្ទៀងផ្ទាត់អ៊ីមែល",
      resendCode: "ផ្ញើលេខកូដម្តងទៀត",
      fullName: "ឈ្មោះពេញ",
      emailAddress: "អាសយដ្ឋានអ៊ីមែល",
      phoneNumber: "លេខទូរស័ព្ទ",
      password: "ពាក្យសម្ងាត់",
      passwordPlaceholder: "បង្កើតពាក្យសម្ងាត់ដែលមានសុវត្ថិភាព",
      passwordHint: "យ៉ាងតិច 6 តួអក្សរ",
      passwordChoiceTitle: "ជ្រើសរើសរបៀបបង្កើតពាក្យសម្ងាត់",
      strongPasswordRequirement: "ប្រើយ៉ាងតិច 10 តួអក្សរ រួមមានអក្សរធំ អក្សរតូច លេខ និងសញ្ញាពិសេស។",
      useSuggestedPassword: "ប្រើពាក្យសម្ងាត់ដែលបានណែនាំ",
      useOwnPassword: "ប្រើពាក្យសម្ងាត់ផ្ទាល់ខ្លួន",
      passwordValidatorTitle: "កម្មវិធីត្រួតពិនិត្យពាក្យសម្ងាត់",
      passwordValidatorSubtitle: "ពាក្យសម្ងាត់របស់អ្នកត្រូវបំពេញតាមលក្ខខណ្ឌទាំងអស់ខាងក្រោម។",
      passwordStrength: {
        label: "កម្រិតរឹងមាំនៃពាក្យសម្ងាត់",
        weak: "ខ្សោយ",
        medium: "មធ្យម",
        strong: "រឹងមាំ",
      },
      passwordRules: {
        upperAndLower: "អក្សរតូច និងអក្សរធំ",
        number: "លេខ (0–9)",
        special: "សញ្ញាពិសេស (!@#$%^&*)",
        length: "យ៉ាងតិច 10 តួអក្សរ",
      },
      agreePrefix: "ខ្ញុំយល់ព្រមនឹង",
      termsOfService: "លក្ខខណ្ឌសេវាកម្ម",
      and: "និង",
      privacyPolicy: "គោលការណ៍ឯកជនភាព",
      creating: "កំពុងបង្កើត...",
      createAccount: "បង្កើតគណនី",
      alternativeSignUp: "ឬចុះឈ្មោះជាមួយ",
      googleSignUp: "ចុះឈ្មោះជាមួយ Google",
      alreadyHaveAccount: "មានគណនីរួចហើយ?",
      signIn: "ចូលគណនី",
      secureRegistration: "ការចុះឈ្មោះមានសុវត្ថិភាព",
      googleConfirmTitle: "បន្តជាមួយ Google?",
      googleConfirmDescription: "Google នឹងត្រូវបានផ្ទៀងផ្ទាត់នៅលើម៉ាស៊ីនមេ មុនពេលបង្កើតគណនីរួចរាល់។",
      googleSelectedAccount: "បន្តជាមួយគណនី Google ដែលអ្នកបានជ្រើស",
      googleChooseAnother: "អ្នកអាចបោះបង់ ហើយជ្រើសគណនីផ្សេងបាន ប្រសិនបើចាំបាច់។",
      cancel: "បោះបង់",
      continue: "បន្ត",
      googleUserAlt: "អ្នកប្រើ Google",
      errors: {
        requiredFields: "សូមបំពេញព័ត៌មានចាំបាច់ទាំងអស់៖ {{fields}}។",
        validEmail: "សូមបញ្ចូលអាសយដ្ឋានអ៊ីមែលត្រឹមត្រូវ។",
        gmailOnly: "ការចុះឈ្មោះអតិថិជនទទួលយកតែអ៊ីមែល @gmail.com ប៉ុណ្ណោះ។",
        validCambodiaPhone: "សូមបញ្ចូលលេខទូរស័ព្ទចល័តកម្ពុជាដែលត្រឹមត្រូវ។",
        passwordLength: "ពាក្យសម្ងាត់ត្រូវមានយ៉ាងតិច 6 តួអក្សរ។",
        strongPassword: "សូមប្រើពាក្យសម្ងាត់ខ្លាំងដែលមានយ៉ាងតិច 10 តួអក្សរ រួមមានអក្សរធំ អក្សរតូច លេខ និងសញ្ញាពិសេស។",
        acceptTerms: "សូមយល់ព្រមនឹងលក្ខខណ្ឌសេវាកម្ម និងគោលការណ៍ឯកជនភាព។",
        registrationFailed: "ការចុះឈ្មោះមិនបានសម្រេច។ សូមព្យាយាមម្តងទៀត។",
        duplicateEmail: "អ៊ីមែលនេះបានចុះឈ្មោះរួចហើយ។ សូមប្រើអ៊ីមែលផ្សេង ឬចូលគណនី។",
        verificationCodeRequired: "សូមបញ្ចូលលេខកូដផ្ទៀងផ្ទាត់ពីអ៊ីមែលរបស់អ្នក។",
        verificationFailed: "មិនអាចផ្ទៀងផ្ទាត់លេខកូដនេះបានទេ។ សូមព្យាយាមម្តងទៀត។",
        resendFailed: "មិនអាចផ្ញើលេខកូដផ្ទៀងផ្ទាត់ម្តងទៀតបានទេ។",
        googleFailed: "ការចុះឈ្មោះជាមួយ Google មិនបានសម្រេច។ សូមព្យាយាមម្តងទៀត។",
        googleMissingToken: "ការឆ្លើយតបពី Google មិនមានសោផ្ទៀងផ្ទាត់ទេ។",
        wrongAccountType: "នេះមិនមែនជាគណនីអតិថិជនទេ។ សូមប្រើព័ត៌មានគណនីត្រឹមត្រូវ។",
      },
      messages: {
        verificationSent: "យើងបានផ្ញើលេខកូដផ្ទៀងផ្ទាត់ទៅអ៊ីមែលរបស់អ្នក។",
        verificationSuccess: "បានផ្ទៀងផ្ទាត់អ៊ីមែលដោយជោគជ័យ។ ឥឡូវនេះអ្នកអាចចូលគណនីបាន។",
        verificationResent: "លេខកូដផ្ទៀងផ្ទាត់ថ្មីត្រូវបានផ្ញើរួចហើយ។",
      },
      fieldNames: {
        fullName: "ឈ្មោះពេញ",
        emailAddress: "អាសយដ្ឋានអ៊ីមែល",
        phoneNumber: "លេខទូរស័ព្ទ",
        password: "ពាក្យសម្ងាត់",
      },
    },
    reviewOrder: {
      loading: "កំពុងផ្ទុកទំព័រវាយតម្លៃ...",
      deliveredOrder: "ការបញ្ជាទិញបានដឹកជញ្ជូនរួច",
      title: "វាយតម្លៃផលិតផលរបស់អ្នក",
      subtitle: "ចែករំលែកការវាយតម្លៃខ្លីៗសម្រាប់ផលិតផលពីការបញ្ជាទិញ #{{orderNumber}}។",
      purchasedProduct: "ផលិតផលដែលបានទិញ",
      quantity: "ចំនួន {{count}}",
      rating: "ការវាយតម្លៃ",
      comment: "មតិយោបល់",
      commentPlaceholder: "ចែករំលែកអ្វីដែលអ្នកចូលចិត្ត របៀបដែលផលិតផលដំណើរការ ឬអ្វីដែលអតិថិជនផ្សេងគួរដឹង។",
      commentHint: "មិនចាំបាច់បំពេញក៏បាន ប៉ុន្តែវាមានប្រយោជន៍សម្រាប់គ្រួសារផ្សេងទៀត។",
      readyTitle: "រួចរាល់ដើម្បីដាក់ស្នើការវាយតម្លៃទាំងអស់ហើយឬនៅ?",
      submitHint: "សូមបន្ថែមការវាយតម្លៃសម្រាប់ផលិតផលទាំង {{count}} មុនពេលដាក់ស្នើ។",
      submittingAll: "កំពុងដាក់ស្នើទាំងអស់...",
      submitAll: "ដាក់ស្នើការវាយតម្លៃទាំងអស់",
      signInOrderAccount: "ចូលគណនីដែលបានបញ្ជាទិញ",
      backToOrders: "ត្រឡប់ទៅការបញ្ជាទិញ",
      status: {
        submitted: "បានដាក់ស្នើ",
        updated: "បានធ្វើបច្ចុប្បន្នភាពការវាយតម្លៃ",
        existing: "បានវាយតម្លៃរួចហើយ",
      },
      notices: {
        alreadyRated: "អ្នកបានវាយតម្លៃផលិតផលនេះរួចហើយ។ អ្នកអាចផ្លាស់ប្តូរផ្កាយ រួចធ្វើបច្ចុប្បន្នភាពការវាយតម្លៃ។",
        submitted: "បានដាក់ស្នើការវាយតម្លៃសម្រាប់ផលិតផលនេះរួចហើយ។ អ្នកអាចបន្តទៅផលិតផលបន្ទាប់ ឬធ្វើបច្ចុប្បន្នភាពផលិតផលនេះ។",
      },
      success: {
        badge: "បានដាក់ស្នើការវាយតម្លៃ",
        title: "សូមអរគុណចំពោះការវាយតម្លៃរបស់អ្នក",
        message: "មតិយោបល់របស់អ្នកបានបង្ហាញហើយ និងជួយឱ្យគ្រួសារផ្សេងៗជ្រើសរើសដោយទំនុកចិត្ត។",
        productReviewed: "ផលិតផលបានវាយតម្លៃ",
        productsReviewed: "ផលិតផលបានវាយតម្លៃ",
        autoReturn: "ត្រឡប់ដោយស្វ័យប្រវត្តិ",
        returningHome: "កំពុងត្រឡប់ទៅទំព័រដើម",
        almostThere: "ជិតរួចរាល់ហើយ",
        continueShopping: "បន្តទិញទំនិញ",
        viewOrders: "មើលការបញ្ជាទិញ",
      },
      errors: {
        loadOrder: "មិនអាចផ្ទុកការបញ្ជាទិញនេះបានទេ។",
        chooseRating: "សូមជ្រើសរើសការវាយតម្លៃ។",
        selectEveryRating: "សូមជ្រើសរើសផ្កាយវាយតម្លៃសម្រាប់ផលិតផលនីមួយៗ មុនពេលដាក់ស្នើ។",
        submitFailed: "ដាក់ស្នើការវាយតម្លៃមិនបានសម្រេច។",
        pageUnavailable: "ទំព័រវាយតម្លៃមិនអាចប្រើបាន",
        wrongAccount: "ការបញ្ជាទិញនេះជារបស់គណនីអតិថិជនផ្សេង។ សូមចូលគណនីដែលបានបញ្ជាទិញនេះ រួចបើកតំណវាយតម្លៃម្តងទៀត។",
        orderNotFound: "រកមិនឃើញការបញ្ជាទិញ។",
      },
    },
    nav: {
      home: "ទំព័រដើម",
      wishlist: "បញ្ជីទំនិញ",
      cart: "កន្រ្តកទំនិញ",
      profile: "ប្រវត្តិរូប",
      orders: "ការបញ្ជាទិញ",
      myOrders: "ការបញ្ជាទិញរបស់ខ្ញុំ",
      settings: "ការកំណត់",
      logout: "ចេញ",
      login: "ចូល",
      register: "ចុះឈ្មោះ",
      browse: "រកមើល",
      account: "គណនី",
      more: "ផ្សេងៗ",
      about: "អំពីយើង",
      contact: "ទំនាក់ទំនង",
      location: "ទីតាំងហាង",
      welcome: "សូមស្វាគមន៍!",
      light: "ភ្លឺ",
      dark: "ងងឹត",
      lightMode: "ភ្លឺ",
      darkMode: "ងងឹត",
      loginRegister: "ចូល/ចុះឈ្មោះ",
      switchToLight: "ប្តូរទៅភ្លឺ",
      switchToDark: "ប្តូរទៅងងឹត",
    },
    product: {
      add: "បន្ថែម",
      addedTitle: "បានបន្ថែមផលិតផល",
      addedSuccess: "បានបន្ថែមផលិតផលដោយជោគជ័យ។",
      addFailedTitle: "បន្ថែមមិនបានសម្រេច",
      addFailed: "បន្ថែមផលិតផលមិនបានសម្រេច",
      all: "ទាំងអស់",
      bestSeller: "លក់ដាច់បំផុត",
      browseFullCollection: "រកមើលផលិតផលទាំងអស់នៅកន្លែងតែមួយ។",
      deal: "ការបញ្ចុះតម្លៃ",
      details: "លម្អិត",
      essentials: "សម្ភារៈចាំបាច់",
      expires: "ផុតកំណត់",
      expiryDate: "កាលបរិច្ឆេទផុតកំណត់",
      exploreEverything: "មើលទាំងអស់",
      freshPicks: "ផលិតផលថ្មីៗដែលទើបបានបន្ថែមទៅកាន់បណ្តុំ។",
      homeCollection: "បណ្តុំទំព័រដើម",
      items: "មុខទំនិញ",
      newArrival: "ផលិតផលថ្មី",
      noProductsFound: "រកមិនឃើញផលិតផល",
      noProductsMessage: "យើងរកមិនឃើញផលិតផលសម្រាប់ការស្វែងរកឬប្រភេទដែលបានជ្រើស។",
      noResultsFound: "រកមិនឃើញលទ្ធផល",
      noResultsMessage: "យើងរកមិនឃើញផលិតផលណាដែលត្រូវនឹងតម្រងបច្ចុប្បន្នរបស់អ្នក។",
      productCatalog: "កាតាឡុកផលិតផល",
      premiumQuality: "គុណភាពខ្ពស់សម្រាប់ជីវិតប្រចាំថ្ងៃរបស់អ្នក។",
      recommendedForYou: "ណែនាំសម្រាប់អ្នក",
      recommendedFromPurchases: "ផ្អែកលើផលិតផលដែលអ្នកធ្លាប់បានទិញ។",
      recommendedFromViews: "ផ្អែកលើផលិតផលដែលអ្នកបានមើលថ្មីៗ។",
      searchPlaceholder: "ស្វែងរកផលិតផល...",
      seeMore: "មើលបន្ថែម",
      showMoreCategories: "បង្ហាញប្រភេទបន្ថែម",
      showingMatches: "កំពុងបង្ហាញអ្វីៗដែលត្រូវនឹង\"{{query}}\"។",
      showingViewMatches: "កំពុងបង្ហាញ{{view}}ដែលត្រូវនឹង\"{{query}}\"។",
      soldOut: "អស់ស្តុក",
      save: "សន្សំ",
      selectSize: "ជ្រើសរើសទំហំ",
      selectSizeMessage: "សូមជ្រើសរើសទំហំ មុនពេលបន្ថែមផលិតផលនេះទៅក្នុងកន្រ្តករបស់អ្នក។",
      chooseSize: "ជ្រើសរើសទំហំ",
      strongestSavings: "ផលិតផលបញ្ចុះតម្លៃបច្ចុប្បន្នដែលសន្សំបានច្រើនបំផុត។",
      popularProducts: "ផលិតផលពេញនិយមដែលត្រូវបានរៀបចំតាមតម្រូវការនិងការវាយតម្លៃ។",
      quickAdd: "បន្ថែមរហ័ស",
      updatedTitle: "បានធ្វើបច្ចុប្បន្នភាពផលិតផល",
      updatedSuccess: "បានធ្វើបច្ចុប្បន្នភាពផលិតផលដោយជោគជ័យ។",
      updateFailedTitle: "ធ្វើបច្ចុប្បន្នភាពមិនបានសម្រេច",
      updateFailed: "ធ្វើបច្ចុប្បន្នភាពមិនបានសម្រេច",
      basedOnReviews: "ផ្អែកលើមតិយោបល់{{count}}",
      reviewCount: "មតិយោបល់{{count}}",
      pleaseLoginToReview: "សូមចូលគណនីដើម្បីសរសេរមតិយោបល់",
      submitReview: "ផ្ញើមតិយោបល់",
      submittingReview: "កំពុងផ្ញើ...",
      excellent: "ល្អឥតខ្ចោះ",
      veryGood: "ល្អណាស់",
      good: "ល្អ",
      poor: "ខ្សោយ",
      bad: "អាក្រក់",
      customerReviews: "មតិយោបល់អតិថិជន",
      viewCustomerReviews: "មើលមតិយោបល់អតិថិជន",
      backToProduct: "ត្រឡប់ទៅផលិតផល",
      reviewsForProduct: "មតិយោបល់សម្រាប់ {{product}}",
      verifiedReviews: "មតិយោបល់ដែលបានផ្ទៀងផ្ទាត់",
      verifiedCustomerFeedback: "មតិយោបល់អតិថិជនដែលបានផ្ទៀងផ្ទាត់",
      deliveredReviewDisplay: "ការវាយតម្លៃ និងមតិយោបល់ពីការទិញដែលបានដឹកជូន នឹងបង្ហាញនៅទីនេះ។",
      showMoreReviews: "បង្ហាញមតិយោបល់បន្ថែម {{count}}",
      showFewerReviews: "បង្ហាញមតិយោបល់តិចជាងមុន",
      noReviewsYet: "មិនទាន់មានមតិយោបល់",
      firstReview: "ធ្វើជាមនុស្សដំបូងដែលផ្តល់មតិយោបល់លើផលិតផលនេះ។",
      writeReview: "សរសេរមតិយោបល់",
      reviewSubmitted: "បានផ្ញើមតិយោបល់",
      feedbackThanks: "អរគុណសម្រាប់មតិយោបល់របស់អ្នក!",
      rating: "ការវាយតម្លៃ",
      selectRating: "ជ្រើសការវាយតម្លៃ",
      yourReview: "មតិយោបល់របស់អ្នក",
      reviewPlaceholder: "សរសេរមតិយោបល់របស់អ្នកនៅទីនេះ...",
      login: "ចូលគណនី",
      youMightAlsoLike: "អ្នកប្រហែលជាចូលចិត្ត",
      loadingProducts: "កំពុងផ្ទុកផលិតផល...",
      noImportChargesCheckout: "មិនមានថ្លៃនាំចូល។ ថ្លៃដឹកជញ្ជូននឹងគណនានៅពេលបង់ប្រាក់។",
      fastLocalDeliveryCambodia: "មានសេវាដឹកជញ្ជូនក្នុងស្រុករហ័សនៅកម្ពុជា",
      deliverSavedAddressCheckout: "ដឹកជញ្ជូនទៅអាសយដ្ឋានដែលអ្នកបានរក្សាទុកនៅពេលបង់ប្រាក់",
      secureCheckoutProtectedPayment: "ការបង់ប្រាក់មានសុវត្ថិភាព និងការពារប្រតិបត្តិការ",
      inStock: "មានក្នុងស្តុក",
      outOfStock: "អស់ស្តុក",
      out: "អស់",
      size: "ទំហំ",
      color: "ពណ៌",
      quantity: "បរិមាណ",
      decreaseQuantity: "បន្ថយបរិមាណ",
      increaseQuantity: "បង្កើនបរិមាណ",
      availableCount: "មាន {{count}}",
      qtyCount: "ចំនួន {{count}}",
      chooseColor: "ជ្រើសរើសពណ៌",
      chooseColorMessage: "សូមជ្រើសរើសពណ៌ផលិតផល មុនពេលបន្ថែមទៅក្នុងកន្រ្តករបស់អ្នក។",
      addToCart: "បន្ថែមទៅកន្រ្តក",
      loginToBuy: "ចូលគណនីដើម្បីទិញ",
      stockLimitReached: "ដល់កំណត់ស្តុកហើយ",
      ok: "យល់ព្រម",
    },
    footer: {
      brandDescription: "គោលដៅដ៏ល្អបំផុតរបស់អ្នកសម្រាប់ផលិតផលគុណភាពនិងបទពិសោធន៍ទិញទំនិញដ៏ល្អ។ធានាគុណភាពនិងពេញចិត្ត។",
      shop: "ទិញទំនិញ",
      allProducts: "ផលិតផលទាំងអស់",
      newArrivals: "ផលិតផលថ្មី",
      bestSellers: "លក់ដាច់បំផុត",
      deals: "ការបញ្ចុះតម្លៃ",
      support: "ជំនួយ",
      contact: "ទំនាក់ទំនង",
      faq: "សំណួរញឹកញាប់",
      trackOrder: "តាមដានការបញ្ជាទិញ",
      company: "ក្រុមហ៊ុន",
      aboutUs: "អំពីយើង",
      location: "ទីតាំង",
      privacyPolicy: "គោលការណ៍ឯកជនភាព",
      termsOfService: "លក្ខខណ្ឌសេវាកម្ម",
      rightsReserved: "រក្សាសិទ្ធិគ្រប់យ៉ាង។",
      secured: "មានសុវត្ថិភាព",
    },
    contact: {
      title: "ទំនាក់ទំនងជំនួយ",
      subtitle: "ទាក់ទងក្រុមការងាររបស់យើង",
      badge: "ទំនាក់ទំនងយើង",
      phone: "ទូរស័ព្ទ",
      email: "អ៊ីមែល",
      telegram: "តេឡេក្រាម",
      voiceSupport: "ជំនួយតាមសំឡេង",
      emailTerminal: "ជំនួយតាមអ៊ីមែល",
      liveProtocol: "ជជែកផ្ទាល់",
      phoneHours: "ច័ន្ទ-សុក្រពីម៉ោង8ព្រឹកដល់5ល្ងាច",
      emailHours: "ឆ្លើយតប24/7",
      directChat: "ជជែកផ្ទាល់",
      instantAgents: "ទំនាក់ទំនងភ្លាមៗជាមួយភ្នាក់ងារ",
      sendMessage: "ផ្ញើសារ",
      formIntro: "មានសំណួរជាក់លាក់មែនទេ?សូមបំពេញទម្រង់ខាងក្រោមដើម្បីផ្ញើព័ត៌មានលម្អិតទៅក្រុមការងាររបស់យើង។",
      fullName: "ឈ្មោះពេញ",
      emailAddress: "អាសយដ្ឋានអ៊ីមែល",
      inquiryTopic: "ប្រធានបទសំណួរ",
      message: "សារ",
      enterName: "បញ្ចូលឈ្មោះ...",
      enterEmail: "បញ្ចូលអ៊ីមែល...",
      yourMessage: "សាររបស់អ្នក...",
      technicalSupport: "ជំនួយបច្ចេកទេស",
      billingFinance: "វិក្កយបត្រនិងហិរញ្ញវត្ថុ",
      partnership: "សំណើសហការ",
      otherGeneral: "ផ្សេងៗ/ទូទៅ",
      businessHours: "ម៉ោងធ្វើការ",
      mondaySunday: "ថ្ងៃច័ន្ទ-អាទិត្យ",
      mondayFriday: "ថ្ងៃច័ន្ទ-សុក្រ",
      saturdaySunday: "ថ្ងៃសៅរ៍-អាទិត្យ",
      publicHolidays: "ថ្ងៃឈប់សម្រាកសាធារណៈ",
      closed: "បិទ",
      sending: "កំពុងផ្ញើ...",
      sendSuccessTitle: "បានផ្ញើសារ",
      sendSuccessMessage: "សារជំនួយរបស់អ្នកត្រូវបានផ្ញើតាមអ៊ីមែលដោយជោគជ័យ។",
      ticketSavedMessage: "សំណើជំនួយរបស់អ្នកត្រូវបានរក្សាទុក។ ការផ្ញើអ៊ីមែលមិនទាន់បានកំណត់ ឬបានបរាជ័យ។",
      sendFailedTitle: "ផ្ញើមិនបានសម្រេច",
      sendFailedMessage: "មិនអាចផ្ញើសាររបស់អ្នកបានទេ។ សូមព្យាយាមម្តងទៀត។",
      requiredFields: "សូមបញ្ចូលឈ្មោះ អ៊ីមែល និងសាររបស់អ្នក។",
    },
    completeProfile: {
      title: "បំពេញប្រវត្តិរូប",
      subtitle: "បន្ថែមលេខទូរស័ព្ទរបស់អ្នកដើម្បីបន្ត",
      prompt: "សូមបន្ថែមលេខទូរស័ព្ទរបស់អ្នកមុនពេលបន្ត។",
      phoneNumber: "លេខទូរស័ព្ទ",
      phoneRequired: "សូមបញ្ចូលលេខទូរស័ព្ទរបស់អ្នក។",
      invalidPhone: "សូមបញ្ចូលលេខទូរស័ព្ទកម្ពុជាដែលត្រឹមត្រូវ។",
      phoneInUse: "លេខទូរស័ព្ទនេះត្រូវបានប្រើដោយគណនីផ្សេងរួចហើយ។",
      saving: "កំពុងរក្សាទុក...",
      savePhone: "រក្សាទុកលេខទូរស័ព្ទ",
      logout: "ចាកចេញ ហើយសាកល្បងពេលក្រោយ",
      successTitle: "បានបំពេញប្រវត្តិរូប",
      savedSuccess: "បានរក្សាទុកលេខទូរស័ព្ទដោយជោគជ័យ។",
      saveFailed: "មិនអាចរក្សាទុកលេខទូរស័ព្ទបានទេ។",
      success: "ជោគជ័យ!",
      redirecting: "កំពុងបញ្ជូនអ្នកទៅទំព័រដើម...",
    },
    about: {
      title: "អំពីយើង",
      subtitle: "កែលម្អបទពិសោធន៍ទិញទំនិញអនឡាញតាំងពីឆ្នាំ 2019",
      badge: "រឿងរ៉ាវរបស់យើង",
      mission: "បេសកកម្មរបស់យើង",
      curatedQuality: "គុណភាពដែលបានជ្រើសរើស",
      missionText: "យើងចង់កែលម្អបទពិសោធន៍ទិញទំនិញអនឡាញរបស់អ្នក ដោយជ្រើសរើសផលិតផលល្អៗដែលជួយបំពេញរបៀបរស់នៅរបស់អ្នក។ យើងប្តេជ្ញាធ្វើឱ្យផលិតផលគុណភាពខ្ពស់ងាយស្រួលទិញបាន ខណៈពេលរក្សាស្តង់ដារខ្ពស់បំផុតនៃសេវាកម្មនិងការពេញចិត្តរបស់អតិថិជន។",
      vision: "ចក្ខុវិស័យរបស់យើង",
      modernEssentials: "សម្ភារៈចាំបាច់សម័យថ្មី",
      visionText: "យើងចង់ក្លាយជាទីផ្សារអនឡាញដែលគួរឱ្យទុកចិត្តបំផុត ដែលគុណភាពជួបជាមួយភាពងាយស្រួល។ យើងមើលឃើញអនាគតដែលអតិថិជនគ្រប់រូបអាចទិញទំនិញដោយទំនុកចិត្ត ដោយដឹងថាពួកគេកំពុងទទួលបានផលិតផលល្អបំផុត តម្លៃសមរម្យ និងសេវាកម្មល្អ។",
      customerFirst: "អតិថិជនជាចម្បង",
      customerFirstSub: "អាទិភាពខ្ពស់បំផុតរបស់យើង",
      globalReach: "ការដឹកជញ្ជូនទូលំទូលាយ",
      globalReachSub: "ដឹកជញ្ជូនទៅគ្រប់ទីកន្លែង",
      yearsOfGrowth: "ឆ្នាំនៃការរីកចម្រើន",
      growthText: "កសាងទំនុកចិត្ត និងផ្តល់គុណភាពដល់អតិថិជនរាប់ពាន់នាក់នៅទូទាំងកម្ពុជា។",
      advantages: "អត្ថប្រយោជន៍របស់យើង",
      whyChooseUs: "ហេតុអ្វីជ្រើសរើសយើង",
      journey: "ដំណើររបស់យើង",
      milestonesTitle: "សមិទ្ធផលសំខាន់ៗ",
      leadership: "ភាពជាអ្នកដឹកនាំ",
      teamTitle: "ក្រុមរបស់យើង",
      stats: {
        dailyProducts: "ផលិតផលប្រចាំថ្ងៃ",
        valueDelivered: "តម្លៃដែលបានផ្តល់",
        happyCustomers: "អតិថិជនពេញចិត្ត",
        trustedBrands: "ម៉ាកដែលគួរឱ្យទុកចិត្ត",
      },
      values: {
        secureTechnology: {
          title: "បច្ចេកវិទ្យាសុវត្ថិភាព",
          description: "សុវត្ថិភាពរបស់អ្នកគឺជាអាទិភាពរបស់យើង។ យើងប្រើការអ៊ិនគ្រីបទំនើប ដើម្បីធានាថាទិន្នន័យនិងប្រតិបត្តិការរបស់អ្នកមានសុវត្ថិភាពជានិច្ច។",
        },
        qualityAssurance: {
          title: "ការធានាគុណភាព",
          description: "យើងជ្រើសរើសទំនិញគ្រប់មុខក្នុងបណ្តុំរបស់យើងដោយយកចិត្តទុកដាក់។ មានតែផលិតផលល្អបំផុតប៉ុណ្ណោះដែលត្រូវបានដាក់លក់ក្នុងហាងរបស់យើង។",
        },
        customerSupport: {
          title: "ជំនួយអតិថិជន",
          description: "ក្រុមជំនួយរបស់យើងត្រៀមជួយអ្នកគ្រប់ពេល។ ការពេញចិត្តរបស់អ្នកត្រូវបានធានា 100%។",
        },
      },
      team: {
        ceoFounder: "នាយកប្រតិបត្តិ និងស្ថាបនិក",
        headOfOperations: "ប្រធានប្រតិបត្តិការ",
        techLead: "ប្រធានបច្ចេកទេស",
        marketingDirector: "នាយកទីផ្សារ",
      },
      milestones: {
        founded: {
          label: "បានបង្កើត",
          detail: "បានចាប់ផ្តើមជាមួយចក្ខុវិស័យក្នុងការផ្លាស់ប្តូរការទិញទំនិញអនឡាញ",
        },
        customers10k: {
          label: "អតិថិជន 10K",
          detail: "សម្រេចបានអតិថិជនពេញចិត្ត 10,000 នាក់នៅទូទាំងកម្ពុជា",
        },
        regionalExpansion: {
          label: "ពង្រីកតំបន់",
          detail: "បានពង្រីកបណ្តាញដឹកជញ្ជូនទៅ 5 ខេត្ត",
        },
        milestone50k: {
          label: "សមិទ្ធផល 50K",
          detail: "បានបម្រើអតិថិជន 50,000 នាក់ជាមួយដៃគូម៉ាកជាង 100",
        },
      },
    },
    empty: {
      noItemsFound: "រកមិនឃើញទំនិញ",
      browseProducts: "មើលផលិតផល",
    },
    cart: {
      shoppingCart: "កន្រ្តកទំនិញ",
      itemCount: "អ្នកមាន {{count}} {{itemLabel}}",
      item: "មុខទំនិញ",
      items: "មុខទំនិញ",
      emptyTitle: "កន្រ្តករបស់អ្នកនៅទទេ",
      emptyMessage: "មើលទៅអ្នកមិនទាន់បានបន្ថែមអ្វីទៅក្នុងកន្រ្តកទេ។",
      emptyHint: "ចាប់ផ្តើមទិញដើម្បីស្វែងរកសម្ភារៈល្អៗសម្រាប់កូនរបស់អ្នក។",
      startShopping: "ចាប់ផ្តើមទិញទំនិញ",
      orderSummary: "សង្ខេបការបញ្ជាទិញ",
      subtotal: "សរុបរង",
      shipping: "ដឹកជញ្ជូន",
      free: "ឥតគិតថ្លៃ",
      tax: "ពន្ធ (8.0%)",
      total: "សរុប",
      inStock: "មានស្តុក",
      sizeValue: "ទំហំ {{size}}",
      decrease: "បន្ថយ",
      increase: "បង្កើន",
      removeItem: "លុបទំនិញ",
      removeAllItems: "លុបទាំងអស់",
      confirmRemoveAllMessage: "តើអ្នកប្រាកដថាចង់លុបទំនិញទាំងអស់ឬ?",
      proceedToCheckout: "បន្តទៅការទូទាត់",
      securePayment: "ការទូទាត់មានសុវត្ថិភាព",
      previewTitle: "កន្រ្តក",
      drawerItemCount: "{{count}} មុខទំនិញ",
      closePreview: "បិទផ្ទាំងកន្រ្តក",
      openWishlist: "បើកបញ្ជីចង់បាន",
      drawerEmptyMessage: "បន្ថែមផលិតផលមួយ ហើយវានឹងបង្ហាញនៅទីនេះ។",
      cartProduct: "ផលិតផលក្នុងកន្រ្តក",
      calculatedAtCheckout: "គណនានៅពេលទូទាត់",
      checkout: "ទូទាត់",
      discountOff: "បញ្ចុះ {{percent}}%",
      loginRequiredTitle: "ត្រូវការចូលគណនី",
      loginRequiredMessage: "សូមចូលគណនីដើម្បីបន្ថែមទំនិញទៅក្នុងកន្រ្តក",
      addedTitle: "បានបន្ថែមទៅកន្រ្តក",
      addedMessage: "ទំនិញនេះ{{size}} ត្រូវបានបន្ថែមទៅក្នុងកន្រ្តករបស់អ្នក។",
      stockLimitMessage: "ទំនិញនេះនៅសល់តែ {{available}} ប៉ុណ្ណោះ ប៉ុន្តែបានស្នើសុំ {{requested}}។ សូមធ្វើបច្ចុប្បន្នភាពកន្រ្តករបស់អ្នក ហើយព្យាយាមម្តងទៀត។",
      actionFailedTitle: "សកម្មភាពមិនបានសម្រេច",
      addFailedMessage: "មិនអាចបន្ថែមទំនិញទៅក្នុងកន្រ្តកបានទេ។ សូមព្យាយាមម្តងទៀត។",
      updateFailedTitle: "ធ្វើបច្ចុប្បន្នភាពមិនបានសម្រេច",
      updateFailedMessage: "មិនអាចធ្វើបច្ចុប្បន្នភាពចំនួនទំនិញបានទេ។",
      removedTitle: "បានលុបទំនិញ",
      removedMessage: "បានលុបទំនិញចេញពីកន្រ្តករបស់អ្នក។",
      removeFailedTitle: "លុបមិនបានសម្រេច",
      removeFailedMessage: "មិនអាចលុបទំនិញចេញពីកន្រ្តកបានទេ។",
      clearFailedTitle: "សម្អាតមិនបានសម្រេច",
      clearFailedMessage: "មិនអាចសម្អាតកន្រ្តកបានទេ។",
    },
    orderDetail: {
      loading: "កំពុងផ្ទុកការបញ្ជាទិញ...",
      notFound: "រកមិនឃើញការបញ្ជាទិញ",
      fetchFailed: "មានបញ្ហាក្នុងការផ្ទុកព័ត៌មានលម្អិតការបញ្ជាទិញ។",
      notAuthenticated: "សូមចូលគណនីដើម្បីមើលការបញ្ជាទិញនេះ។",
      back: "ត្រឡប់ក្រោយ",
      backToOrders: "ត្រឡប់ទៅការបញ្ជាទិញ",
      backToHome: "ត្រឡប់ទៅទំព័រដើម",
      orderNumber: "ការបញ្ជាទិញ #{{number}}",
      items: "ទំនិញ",
      qty: "ចំនួន",
      size: "ទំហំ",
      color: "ពណ៌",
      shipping: "ដឹកជញ្ជូន",
      payment: "ការទូទាត់",
      method: "វិធីសាស្ត្រ",
      paymentStatus: "ស្ថានភាពទូទាត់",
      paidOn: "បានបង់នៅ",
      summary: "សង្ខេប",
      subtotal: "សរុបរង",
      free: "ឥតគិតថ្លៃ",
      tax: "ពន្ធ",
      total: "សរុប",
      undefined: "មិនបានកំណត់",
      cancelOrder: "បោះបង់ការបញ្ជាទិញ",
      cancelling: "កំពុងបោះបង់...",
      cancelConfirmTitle: "បោះបង់ការបញ្ជាទិញនេះ?",
      cancelConfirm: "តើអ្នកចង់បោះបង់ការបញ្ជាទិញនេះមែនទេ? អ្នកអាចបោះបង់បានតែមុនពេលអ្នកលក់បញ្ជាក់ការបញ្ជាទិញ។",
      keepOrder: "រក្សាការបញ្ជាទិញ",
      confirmCancel: "បាទ/ចាស បោះបង់ការបញ្ជាទិញ",
      cancelFailed: "មិនអាចបោះបង់ការបញ្ជាទិញបានទេ",
      cancelledNotice: "ការបញ្ជាទិញនេះត្រូវបានបោះបង់។",
      status: {
        pending: "កំពុងរង់ចាំ",
        processing: "កំពុងដំណើរការ",
        shipped: "បានដឹកចេញ",
        delivered: "បានដឹកជូន",
        cancelled: "បានបោះបង់",
        paid: "បានបង់ប្រាក់",
        failed: "បរាជ័យ",
        refunded: "បានសងប្រាក់",
      },
      paymentMethods: {
        cashondelivery: "បង់ប្រាក់ពេលទទួលទំនិញ",
        bakongkhqr: "បាគង KHQR",
        undefined: "មិនបានកំណត់",
      },
    },
    orderTracking: {
      title: "តាមដានការបញ្ជាទិញ",
      subtitle: "បញ្ចូលលេខបញ្ជាទិញរបស់អ្នកដើម្បីពិនិត្យស្ថានភាពបច្ចុប្បន្ននិងដំណើរការដឹកជញ្ជូន",
      badge: "ការតាមដាន",
      placeholder: "បញ្ចូលលេខបញ្ជាទិញពេញ ឬ 8 តួចុងក្រោយ",
      track: "តាមដាន",
      tracking: "កំពុងតាមដាន...",
      order: "ការបញ្ជាទិញ",
      status: "ស្ថានភាព",
      estimatedDelivery: "ពេលដឹកជញ្ជូនប៉ាន់ស្មាន",
      deliveredOn: "បានដឹកជូននៅ",
      trackingId: "លេខតាមដាន",
      storeDelivery: "ការដឹកជញ្ជូនរបស់ហាង",
      trackingHistory: "ប្រវត្តិតាមដាន",
      pending: "កំពុងរង់ចាំ",
      processed: "បានដំណើរការ",
      destination: "គោលដៅ",
      recipient: "អ្នកទទួល",
      deliveryAddress: "អាសយដ្ឋានដឹកជញ្ជូន",
      customer: "អតិថិជន",
      timeline: {
        orderPlaced: "បានដាក់ការបញ្ជាទិញ",
        orderProcessed: "បានដំណើរការការបញ្ជាទិញ",
        shipped: "បានដឹកចេញ",
        delivered: "បានដឹកជូន",
      },
      locations: {
        onlineCheckout: "ការទូទាត់តាមអនឡាញ",
        sellerConfirmation: "ការបញ្ជាក់ពីអ្នកលក់",
        readyForDelivery: "រួចរាល់សម្រាប់ដឹកជញ្ជូន",
      },
      errors: {
        enterOrderNumber: "សូមបញ្ចូលលេខបញ្ជាទិញ។",
        notFound: "រកមិនឃើញការបញ្ជាទិញ។",
        notAuthorized: "អ្នកមិនមានសិទ្ធិតាមដានការបញ្ជាទិញនេះទេ។",
        trackFailed: "មិនអាចតាមដានការបញ្ជាទិញនេះបានទេ។ សូមពិនិត្យលេខបញ្ជាទិញហើយព្យាយាមម្តងទៀត។",
      },
    },
    ordersPage: {
      loading: "កំពុងធ្វើសមកាលកម្មប្រវត្តិ...",
      identityNeeded: "ត្រូវការអត្តសញ្ញាណ",
      identityMessage: "សូមចូលគណនីដើម្បីមើលប្រវត្តិការបញ្ជាទិញ។",
      enterVault: "ចូលមើល",
      goBack: "ត្រឡប់ក្រោយ",
      myOrders: "ការបញ្ជាទិញរបស់ខ្ញុំ",
      orderHistory: "ប្រវត្តិការបញ្ជាទិញ",
      historySubtitle: "មើលព័ត៌មានលម្អិតនៃការបញ្ជាទិញមុនៗរបស់អ្នក",
      ordersPlaced: "បានដាក់ការបញ្ជាទិញ {{count}}",
      noOrdersFound: "រកមិនឃើញការបញ្ជាទិញ",
      noOrdersMessage: "អ្នកមិនទាន់បានដាក់ការបញ្ជាទិញនៅឡើយទេ។ ចាប់ផ្តើមទិញទំនិញដើម្បីស្វែងរកទំនិញល្អៗ។",
      startShopping: "ចាប់ផ្តើមទិញទំនិញ",
      itemsCount: "ទំនិញ ({{count}})",
      shippingTo: "ដឹកជញ្ជូនទៅ",
      mapLocationSelected: "បានជ្រើសរើសទីតាំងលើផែនទី",
      viewDetails: "មើលព័ត៌មានលម្អិត",
      errors: {
        notAuthenticated: "មិនទាន់បានផ្ទៀងផ្ទាត់",
        fetchFailed: "មិនអាចទាញយកការបញ្ជាទិញបានទេ",
      },
    },
    profile: {
      accessDenied: "មិនអនុញ្ញាតឱ្យចូល",
      accessDeniedMessage: "សូមចូលគណនីដើម្បីមើលផ្ទាំងប្រវត្តិរូបនិងគ្រប់គ្រងគណនី។",
      signInNow: "ចូលគណនីឥឡូវនេះ",
      change: "ប្តូរ",
      success: "ជោគជ័យ",
      error: "កំហុស",
      overview: "ទិដ្ឋភាពទូទៅ",
      profileDetails: "ព័ត៌មានប្រវត្តិរូប",
      securityLogin: "សុវត្ថិភាពនិងការចូល",
      totalOrders: "ការបញ្ជាទិញសរុប",
      wishlistItems: "ទំនិញក្នុងបញ្ជី",
      itemsInCart: "ទំនិញក្នុងកន្រ្តក",
      viewAll: "មើលទាំងអស់",
      accountSummary: "សង្ខេបគណនី",
      edit: "កែប្រែ",
      fullName: "ឈ្មោះពេញ",
      email: "អ៊ីមែល",
      phone: "ទូរស័ព្ទ",
      joined: "បានចូលរួម",
      notProvided: "មិនបានផ្តល់",
      recentActivity: "សកម្មភាពថ្មីៗ",
      noOrdersYet: "មិនទាន់មានការបញ្ជាទិញ",
      noOrdersMessage: "នៅពេលអ្នកទិញ វានឹងបង្ហាញនៅទីនេះ។",
      startShopping: "ចាប់ផ្តើមទិញទំនិញ",
      updateProfile: "ធ្វើបច្ចុប្បន្នភាពប្រវត្តិរូប",
      updateProfileDescription: "គ្រប់គ្រងព័ត៌មានផ្ទាល់ខ្លួននិងព័ត៌មានទំនាក់ទំនង។",
      editProfile: "កែប្រែប្រវត្តិរូប",
      cancel: "បោះបង់",
      emailAddress: "អាសយដ្ឋានអ៊ីមែល",
      phoneNumber: "លេខទូរស័ព្ទ",
      saving: "កំពុងរក្សាទុក...",
      saveChanges: "រក្សាទុកការផ្លាស់ប្តូរ",
      securitySettings: "ការកំណត់សុវត្ថិភាព",
      securityDescription: "ធ្វើបច្ចុប្បន្នភាពពាក្យសម្ងាត់ដើម្បីរក្សាគណនីឱ្យមានសុវត្ថិភាព។",
      currentPassword: "ពាក្យសម្ងាត់បច្ចុប្បន្ន",
      newPassword: "ពាក្យសម្ងាត់ថ្មី",
      confirmNewPassword: "បញ្ជាក់ពាក្យសម្ងាត់ថ្មី",
      enterCurrentPassword: "បញ្ចូលពាក្យសម្ងាត់បច្ចុប្បន្ន",
      enterNewPassword: "បញ្ចូលពាក្យសម្ងាត់ថ្មី",
      confirmNewPasswordPlaceholder: "បញ្ជាក់ពាក្យសម្ងាត់ថ្មី",
      updatingSecurity: "កំពុងធ្វើបច្ចុប្បន្នភាពសុវត្ថិភាព...",
      updatePassword: "ធ្វើបច្ចុប្បន្នភាពពាក្យសម្ងាត់",
      emailStep: "អ៊ីមែល",
      verifyStep: "ផ្ទៀងផ្ទាត់",
      passwordStep: "ពាក្យសម្ងាត់",
      enterAccountEmail: "បញ្ចូលអ៊ីមែលគណនីរបស់អ្នក",
      sendingCode: "កំពុងផ្ញើលេខកូដ...",
      sendVerificationCode: "ផ្ញើលេខកូដផ្ទៀងផ្ទាត់",
      verificationCode: "លេខកូដផ្ទៀងផ្ទាត់",
      codeSentTo: "យើងបានផ្ញើលេខកូដ 6 ខ្ទង់ទៅកាន់ {{email}}។",
      verifying: "កំពុងផ្ទៀងផ្ទាត់...",
      verifyCode: "ផ្ទៀងផ្ទាត់លេខកូដ",
      resendIn: "ផ្ញើម្តងទៀតក្នុង {{seconds}} វិនាទី",
      resendCode: "ផ្ញើលេខកូដម្តងទៀត",
      startOver: "ចាប់ផ្តើមម្តងទៀត",
      enterEmailRequired: "សូមបញ្ចូលអាសយដ្ឋានអ៊ីមែលរបស់អ្នក។",
      accountEmailRequired: "សូមបញ្ចូលអាសយដ្ឋានអ៊ីមែលរបស់គណនីនេះ។",
      verificationCodeSent: "លេខកូដផ្ទៀងផ្ទាត់ត្រូវបានផ្ញើទៅអ៊ីមែលរបស់អ្នក។",
      sendVerificationFailed: "មិនអាចផ្ញើលេខកូដផ្ទៀងផ្ទាត់បានទេ។",
      enterSixDigitCode: "សូមបញ្ចូលលេខកូដផ្ទៀងផ្ទាត់ 6 ខ្ទង់។",
      codeVerified: "បានផ្ទៀងផ្ទាត់លេខកូដ។ សូមបញ្ចូលពាក្យសម្ងាត់ថ្មីរបស់អ្នក។",
      verifyCodeFailed: "មិនអាចផ្ទៀងផ្ទាត់លេខកូដបានទេ។",
      newVerificationCodeSent: "លេខកូដផ្ទៀងផ្ទាត់ថ្មីត្រូវបានផ្ញើ។",
      resendCodeFailed: "មិនអាចផ្ញើលេខកូដផ្ទៀងផ្ទាត់ម្តងទៀតបានទេ។",
      verifyEmailFirst: "សូមផ្ទៀងផ្ទាត់លេខកូដអ៊ីមែលរបស់អ្នកជាមុនសិន។",
      passwordUpdated: "បានធ្វើបច្ចុប្បន្នភាពពាក្យសម្ងាត់ដោយជោគជ័យ។",
      passwordUpdateFailed: "មិនអាចធ្វើបច្ចុប្បន្នភាពពាក្យសម្ងាត់បានទេ។",
      avatarTooLarge: "រូបភាពប្រវត្តិរូបត្រូវតែតិចជាង 5MB",
      notAuthenticated: "មិនទាន់បានផ្ទៀងផ្ទាត់",
      currentPasswordRequired: "ត្រូវការពាក្យសម្ងាត់បច្ចុប្បន្នដើម្បីប្តូរពាក្យសម្ងាត់",
      passwordsDoNotMatch: "ពាក្យសម្ងាត់ថ្មីមិនដូចគ្នា",
      passwordTooShort: "ពាក្យសម្ងាត់ថ្មីត្រូវមានយ៉ាងតិច 6 តួអក្សរ",
      strongPasswordRequired: "សូមប្រើយ៉ាងតិច 10 តួអក្សរ រួមមានអក្សរធំ អក្សរតូច លេខ និងសញ្ញាពិសេស។",
      profileUpdated: "បានធ្វើបច្ចុប្បន្នភាពប្រវត្តិរូបដោយជោគជ័យ!",
      updateFailed: "ធ្វើបច្ចុប្បន្នភាពប្រវត្តិរូបមិនបានសម្រេច",
      myProfile: "ប្រវត្តិរូបរបស់ខ្ញុំ",
      myOrders: "ការបញ្ជាទិញរបស់ខ្ញុំ",
      wishlist: "បញ្ជីទំនិញ",
      settings: "ការកំណត់",
      signOut: "ចេញពីគណនី",
      administrator: "អ្នកគ្រប់គ្រង",
      valuedCustomer: "អតិថិជនមានតម្លៃ",
    },
    mapPicker: {
      selectLocationOnMap: "ជ្រើសរើសទីតាំង",
      updateLocationOnMap: "ធ្វើបច្ចុប្បន្នភាពទីតាំង",
      selectLocation: "ជ្រើសរើសទីតាំង",
      loadingMap: "កំពុងផ្ទុកផែនទី...",
      pleaseWait: "សូមរង់ចាំបន្តិច",
      searchPlaceholder: "ស្វែងរកទីតាំង...",
      useCurrentLocation: "ប្រើទីតាំងបច្ចុប្បន្ន",
      currentLocation: "ទីតាំងបច្ចុប្បន្ន",
      deliveryLocation: "ទីតាំងដឹកជញ្ជូន",
      mapHint: "ចុចលើផែនទី ឬអូសសញ្ញាសម្គាល់",
      cancel: "បោះបង់",
      saving: "កំពុងរក្សាទុក...",
      confirmLocation: "បញ្ជាក់ទីតាំង",
      cambodiaOnly: "សូមជ្រើសទីតាំងនៅក្នុងប្រទេសកម្ពុជាប៉ុណ្ណោះ។",
      mapInitFailed: "ផ្ទុកផែនទីមិនបានសម្រេច។ សូមព្យាយាមម្តងទៀត។",
      googleMapsMissingKey: "បាត់ Google Maps API key។ សូមកំណត់ VITE_GOOGLE_MAPS_API_KEY ក្នុង environment របស់អ្នក។",
      googleMapsAuthFailed: "Google Maps មិនត្រូវបានអនុញ្ញាតសម្រាប់គេហទំព័រនេះទេ។ សូមបើក billing និងអនុញ្ញាត {{origin}}/* ក្នុងការកំណត់ API key របស់ Google Maps។",
      googleMapsLoadFailed: "ផ្ទុក Google Maps មិនបានសម្រេច។ សូមពិនិត្យ API key ហើយព្យាយាមម្តងទៀត។",
      searchNotFound: "រកមិនឃើញទីតាំង។ សូមព្យាយាមបញ្ចូលទីកន្លែងឱ្យច្បាស់ជាងនេះ។",
      geolocationUnsupported: "កម្មវិធីរុករករបស់អ្នកមិនគាំទ្រការរកទីតាំងទេ។",
      detectionFailed: "មិនអាចរកទីតាំងរបស់អ្នកបានទេ។",
      locationDenied: "ការចូលប្រើទីតាំងត្រូវបានបដិសេធ។ សូមអនុញ្ញាតទីតាំងសម្រាប់គេហទំព័រនេះក្នុងការកំណត់កម្មវិធីរុករក។",
      locationUnavailable: "ទីតាំងបច្ចុប្បន្នមិនអាចប្រើបាន។ សូមបើកសេវាកម្មទីតាំង ហើយព្យាយាមម្តងទៀត។",
    },
    checkout: {
      shippingAddress: "អាសយដ្ឋានដឹកជញ្ជូន",
      fullName: "ឈ្មោះពេញ",
      enterFullName: "បញ្ចូលឈ្មោះពេញរបស់អ្នក",
      pinLocation: "កំណត់ទីតាំង",
      locationSelected: "បានជ្រើសរើសទីតាំង",
      optional: "មិនចាំបាច់",
      street: "ផ្លូវ",
      streetPlaceholder: "បញ្ចូលឈ្មោះ ឬលេខផ្លូវ",
      address: "អាសយដ្ឋាន",
      addressPlaceholder: "បញ្ចូលព័ត៌មានអាសយដ្ឋាន",
      selectLocationOnMap: "ជ្រើសរើសទីតាំង",
      cityProvince: "ទីក្រុង/ខេត្ត",
      cityProvincePlaceholder: "បញ្ចូលទីក្រុង ឬខេត្ត",
      phoneNumber: "លេខទូរស័ព្ទ",
      orderSuccessTitle: "បានដាក់ការបញ្ជាទិញដោយជោគជ័យ!",
      orderSuccessMessage: "អរគុណសម្រាប់ការបញ្ជាទិញរបស់អ្នក។ យើងកំពុងរៀបចំទំនិញរបស់អ្នកសម្រាប់ការដឹកជញ្ជូន។",
      orderId: "លេខបញ្ជាទិញ",
      orderCreated: "បានបង្កើតការបញ្ជាទិញ",
      cancelNoticeTitle: "ត្រូវការបោះបង់មែនទេ?",
      cancelNoticeMessage: "អ្នកអាចបោះបង់ការបញ្ជាទិញនេះពីការបញ្ជាទិញរបស់ខ្ញុំ រហូតដល់អ្នកលក់បញ្ជាក់វា។",
      viewOrderDetails: "មើលព័ត៌មានលម្អិតការបញ្ជាទិញ",
      continueShopping: "បន្តទិញទំនិញ",
      bakongQrNotice: "ជ្រើសរើស បាគង KHQR បន្ទាប់មកចុច ដាក់ការបញ្ជាទិញ និងបង្កើត QR កូដ។ QR កូដដែលមានសុវត្ថិភាព និងចំនួនទឹកប្រាក់ត្រឹមត្រូវនឹងបង្ហាញ។",
      bakongCancellationWarning: "សំខាន់៖ បន្ទាប់ពីការទូទាត់តាមបាគង KHQR បានជោគជ័យ ការបញ្ជាទិញនេះមិនអាចបោះបង់បានទេ។",
      bakongWarningTitle: "សូមបញ្ជាក់មុនពេលបង់ប្រាក់",
      bakongWarningDetail: "អ្នកអាចត្រឡប់ក្រោយ ឬបោះបង់មុនពេលការទូទាត់បានជោគជ័យ។ បន្ទាប់ពីបាគងបញ្ជាក់ការទូទាត់ ការបញ្ជាទិញនឹងក្លាយជាស្ថាពរ និងមិនអាចបោះបង់បានទេ។",
      chooseAnotherMethod: "ជ្រើសរើសវិធីផ្សេង",
      understandContinue: "ខ្ញុំយល់ព្រម បន្ត",
      bakongWarningClose: "បិទការជូនដំណឹងការទូទាត់",
      generatingQr: "កំពុងបង្កើត QR កូដ...",
      processing: "កំពុងដំណើរការ...",
      placeOrderGenerateQr: "ដាក់ការបញ្ជាទិញ និងបង្កើត QR កូដ",
      placeOrder: "ដាក់ការបញ្ជាទិញ",
      errors: {
        incompleteShipping: "សូមបំពេញព័ត៌មានដឹកជញ្ជូនរបស់អ្នកមុនពេលដាក់ការបញ្ជាទិញ។",
        missingLocation: "សូមជ្រើសរើសទីតាំងដឹកជញ្ជូនរបស់អ្នកលើផែនទីមុនពេលដាក់ការបញ្ជាទិញ។",
        emptyCart: "កន្រ្តករបស់អ្នកនៅទទេ។ សូមបន្ថែមទំនិញមុនពេលដាក់ការបញ្ជាទិញ។",
      },
    },
    bakongPayment: {
      generatingSecureQr: "កំពុងបង្កើត QR កូដសុវត្ថិភាពរបស់អ្នក…",
      paymentError: "មានបញ្ហាក្នុងការទូទាត់",
      tryAgain: "ព្យាយាមម្តងទៀត",
      backToOrder: "ត្រឡប់ទៅការបញ្ជាទិញ",
      backToCheckout: "ត្រឡប់ទៅទំព័រទូទាត់",
      paymentSuccessful: "ការទូទាត់ជោគជ័យ!",
      paymentConfirmed: "ការទូទាត់របស់អ្នកត្រូវបានបញ្ជាក់។ កំពុងបញ្ជូនទៅការបញ្ជាទិញរបស់អ្នក…",
      redirecting: "កំពុងបញ្ជូនក្នុងរយៈពេលបន្តិចទៀត",
      paymentFailed: "ការទូទាត់បរាជ័យ",
      paymentFailedMessage: "មានបញ្ហាកើតឡើង។ សូមព្យាយាមម្តងទៀត ឬជ្រើសរើសវិធីទូទាត់ផ្សេង។",
      qrExpired: "QR កូដបានផុតកំណត់",
      qrExpiredMessage: "QR កូដរបស់អ្នកបានផុតកំណត់បន្ទាប់ពី 5 នាទី។ សូមបង្កើត QR កូដថ្មីដើម្បីបញ្ចប់ការទូទាត់។",
      generating: "កំពុងបង្កើត…",
      newQrCode: "QR កូដថ្មី",
      back: "ត្រឡប់ក្រោយ",
      scanToPay: "ស្កេនដើម្បីបង់ប្រាក់",
      waitingForPayment: "កំពុងរង់ចាំការទូទាត់",
      live: "បន្តផ្ទាល់",
      expiresIn: "ផុតកំណត់ក្នុង",
      scanInstruction: "ស្កេនជាមួយបាគង ឬកម្មវិធីធនាគារដែលគាំទ្រ KHQR",
      amountDue: "ចំនួនទឹកប្រាក់ត្រូវបង់",
      currency: "រូបិយប័ណ្ណ",
      usd: "ដុល្លារអាមេរិក",
      khr: "រៀលខ្មែរ",
      merchant: "អ្នកទទួលប្រាក់",
      order: "ការបញ្ជាទិញ",
      status: "ស្ថានភាព",
      checkingAutomatically: "កំពុងពិនិត្យដោយស្វ័យប្រវត្តិ",
      paymentStatusUpdates: "ស្ថានភាពទូទាត់ធ្វើបច្ចុប្បន្នភាពដោយស្វ័យប្រវត្តិ",
      payWithBankingApp: "បង់ប្រាក់ជាមួយកម្មវិធីធនាគារ",
      saveQr: "រក្សាទុក ឬចែករំលែក QR",
      shareQrTitle: "KHQR របស់ Cherish Baby Store",
      shareQrText: "រក្សាទុក KHQR នេះ រួចជ្រើសរើសរូបភាពពីកម្មវិធីស្កេន QR របស់ធនាគារ។",
      qrSavedTitle: "បានរក្សាទុក QR",
      qrSavedMessage: "បើកកម្មវិធីធនាគារ ហើយជ្រើសរើស QR ពីរូបថត ឬ Gallery។",
      qrSaveFailedTitle: "មិនអាចរក្សាទុក QR បាន",
      qrSaveFailedMessage: "ចុចសង្កត់លើរូបភាព QR រួចជ្រើសរើសរក្សាទុកទៅរូបថត។",
      samePhoneHelp: "ចង់បង់ប្រាក់លើទូរស័ព្ទនេះ?",
      samePhoneStep1: "ចុច «រក្សាទុក ឬចែករំលែក QR» ហើយរក្សាទុករូបភាព។",
      samePhoneStep2: "បើក ABA, ACLEDA ឬកម្មវិធីធនាគារដែលគាំទ្រ KHQR។",
      samePhoneStep3: "បើកកម្មវិធីស្កេន QR ជ្រើសរើសពីរូបថត/Gallery រួចបញ្ជាក់ការទូទាត់។",
      refresh: "បង្កើតថ្មី",
      cancelling: "កំពុងបោះបង់…",
      cancelPayment: "បោះបង់ការទូទាត់",
      securityNote: "ការពារដោយ NBC Bakong · ទទួលដោយធនាគារដែលគាំទ្រ KHQR ទាំងអស់",
      qrAlt: "QR កូដទូទាត់តាមបាគង KHQR",
    },
    wishlistAlerts: {
      loginRequired: "ត្រូវការចូលគណនី",
      loginToAdd: "សូមចូលគណនីដើម្បីបន្ថែមទំនិញទៅបញ្ជីទំនិញរបស់អ្នក",
      loginToManage: "សូមចូលគណនីដើម្បីគ្រប់គ្រងបញ្ជីទំនិញរបស់អ្នក",
      thisProduct: "ផលិតផលនេះ",
      savedTitle: "បានរក្សាទុកទៅបញ្ជីទំនិញ",
      savedMessage: "{{product}} ត្រូវបានរក្សាទុក។",
      removedTitle: "បានលុបចេញពីបញ្ជីទំនិញ",
      removedMessage: "ទំនិញត្រូវបានលុបចេញពីបញ្ជីទំនិញរបស់អ្នក។",
      actionFailed: "សកម្មភាពបរាជ័យ",
      addFailed: "មិនអាចបន្ថែមទំនិញទៅបញ្ជីទំនិញបានទេ។",
      removeFailedTitle: "លុបមិនបានសម្រេច",
      removeFailed: "មិនអាចលុបទំនិញចេញពីបញ្ជីទំនិញបានទេ។",
    },
    wishlistGuest: {
      privateList: "បញ្ជីចង់បានផ្ទាល់ខ្លួនរបស់អ្នក",
      title: "សូមចូលគណនី ឬចុះឈ្មោះ ដើម្បីមើលបញ្ជីចង់បាន",
      description: "បង្កើតគណនី ឬចូលគណនី ដើម្បីរក្សាទុកផលិតផលដែលអ្នកចូលចិត្ត និងងាយស្រួលស្វែងរកវិញនៅលើឧបករណ៍ណាក៏បាន។",
      login: "ចូលគណនី",
      register: "ចុះឈ្មោះ",
      accountNote: "ផលិតផលដែលអ្នកបានរក្សាទុក នឹងត្រូវបានរក្សាសុវត្ថិភាពជាមួយគណនីរបស់អ្នក។",
    },
    location: {
      title: "វត្តមានសកល",
      subtitle: "ហេដ្ឋារចនាសម្ព័ន្ធនិងមជ្ឈមណ្ឌលយុទ្ធសាស្ត្រ",
      badge: "ទីតាំងសាខា",
      globalHeadquarters: "ការិយាល័យកណ្តាលសកល",
      europeanHub: "មជ្ឈមណ្ឌលឡូជីស្ទិកអឺរ៉ុប",
      operational: "កំពុងដំណើរការ",
      standby: "រង់ចាំ",
      syncStatus: "ស្ថានភាពសមកាលកម្ម",
      globalNetworkActive: "បណ្តាញសកលកំពុងដំណើរការ",
      activeHubs: "មជ្ឈមណ្ឌលសកម្ម: 02 //ចំណុចបណ្តាញ: 48",
      primaryEntities: "អង្គភាពចម្បង",
      requestDirections: "ស្នើសុំទិសដៅ",
      worldwideFulfillment: "ការបំពេញការបញ្ជាទិញទូទាំងពិភពលោក",
      fulfillmentText: "បណ្តាញឡូជីស្ទិករបស់យើងគ្របដណ្តប់6ទ្វីបដោយប្រើចំណុចចែកចាយជាង150ដើម្បីធានាថាទំនិញរបស់អ្នកទៅដល់តាមវិធីមានប្រសិទ្ធភាពបំផុត។",
      uplinkDirectly: "ទាក់ទងផ្ទាល់",
    },
    help: {
      title: "មជ្ឈមណ្ឌលជំនួយ",
      subtitle: "ស្វែងរកចម្លើយមគ្គុទ្ទេសក៍និងជំនួយផ្ទាល់បានលឿនជាងមុន",
      badge: "ជំនួយ",
      customerSupportHub: "មជ្ឈមណ្ឌលជំនួយអតិថិជន",
      howCanWeHelp: "តើយើងអាចជួយអ្នកដូចម្តេច?",
      intro: "ស្វែងរកតាមប្រធានបទឬសំណួរដើម្បីរកចម្លើយបានលឿនជាងមុន។",
      searchPlaceholder: "ស្វែងរកប្រធានបទឬសំណួរ...",
      tracking: "តាមដាន",
      warranty: "ការធានា",
      deliveryTime: "ពេលវេលាដឹកជញ្ជូន",
      security: "សុវត្ថិភាព",
      orderChanges: "ការកែប្រែការបញ្ជាទិញ",
      coverage: "ការគាំទ្រ",
      selfService: "ជំនួយដោយខ្លួនឯង",
      topics: "ប្រធានបទ",
      commonIssues: "គ្របដណ្តប់បញ្ហាទូទៅ",
      needPerson: "ត្រូវការបុគ្គលិកមែនទេ?",
      contactSupport: "ទាក់ទងជំនួយ",
      escalateIssue: "បញ្ជូនបញ្ហាទៅក្រុមជំនួយ",
      browseByTopic: "រកមើលតាមប្រធានបទ",
      faq: "សំណួរញឹកញាប់",
      searchResults: "លទ្ធផលស្វែងរក",
      questionsMatch: "សំណួរត្រូវនឹងការស្វែងរកបច្ចុប្បន្នរបស់អ្នក។",
      supportRoute: "ផ្លូវជំនួយ",
      liveAssistance: "ជំនួយផ្ទាល់",
      moveToContact: "បន្តទៅទំនាក់ទំនងប្រសិនបើត្រូវការ។",
      category: "ប្រភេទ",
      topic: "ប្រធានបទ",
      articles: "អត្ថបទ",
      article: "អត្ថបទ",
      questions: "សំណួរ",
      question: "សំណួរ",
      noMatches: "មិនមានលទ្ធផល",
      noArticles: "មិនមានអត្ថបទត្រូវនឹងការស្វែងរកនោះទេ។",
      noArticlesHint: "សាកល្បងពាក្យទូលំទូលាយដូចជាតាមដាន ការធានា ដឹកជញ្ជូន ឬគណនី។ ប្រសិនបើបញ្ហាជាក់លាក់ សូមទាក់ទងជំនួយដោយផ្ទាល់។",
      noQuestions: "មិនមានសំណួរត្រូវនឹងការស្វែងរកនោះទេ។",
      noQuestionsHint: "សាកល្បងពាក្យទូលំទូលាយដូចជាតាមដាន ការធានា ដឹកជញ្ជូន ការបញ្ជាទិញ ឬការទូទាត់។ ប្រសិនបើបញ្ហាជាក់លាក់ សូមទាក់ទងជំនួយដោយផ្ទាល់។",
      escalationPath: "ផ្លូវបញ្ជូនបន្ត",
      stillNeedHelp: "នៅតែត្រូវការជំនួយជាមួយការបញ្ជាទិញ គណនី ឬការធានាមែនទេ?",
      escalationText: "ប្រើទំព័រទំនាក់ទំនងសម្រាប់បញ្ហាដែលត្រូវការចូលដំណើរការគណនីពិនិត្យការបញ្ជាទិញដោយដៃឬពិនិត្យវិក្កយបត្រ។",
      ordersProducts: "ការបញ្ជាទិញនិងផលិតផល",
      ordersProductsDescription: "ការកែប្រែការបញ្ជាទិញសំណួរស្តុកជំនួយទំហំនិងការថែទាំផលិតផល។",
      orderEdits: "កែការបញ្ជាទិញ",
      itemAvailability: "ភាពមានស្តុក",
      productQuality: "គុណភាពផលិតផល",
      shippingDelivery: "ដឹកជញ្ជូននិងប្រគល់ទំនិញ",
      shippingDeliveryDescription: "ពេលវេលាតាមដានវិធីដឹកជញ្ជូនការពន្យារពេលនិងការខកខានប្រគល់ទំនិញ។",
      trackPackage: "តាមដានកញ្ចប់",
      deliveryWindow: "ពេលវេលាប្រគល់",
      shippingOptions: "ជម្រើសដឹកជញ្ជូន",
      paymentsBilling: "ការទូទាត់និងវិក្កយបត្រ",
      paymentsBillingDescription: "វិធីបង់ប្រាក់ដែលទទួលយក បញ្ហាវិក្កយបត្រ និងការបញ្ជាក់ការទូទាត់។",
      billingIssues: "បញ្ហាវិក្កយបត្រ",
      warrantySupport: "ជំនួយការធានា",
      paymentMethods: "វិធីបង់ប្រាក់",
      securityPrivacy: "សុវត្ថិភាពនិងឯកជនភាព",
      securityPrivacyDescription: "ការពារគណនីការគ្រប់គ្រងឯកជនភាពនិងដំណើរការផ្ទៀងផ្ទាត់។",
      accountSecurity: "សុវត្ថិភាពគណនី",
      privacyRequests: "សំណើឯកជនភាព",
      verification: "ការផ្ទៀងផ្ទាត់",
      trackOrderQuestion: "តើខ្ញុំតាមដានការបញ្ជាទិញដូចម្តេច?",
      trackOrderAnswer: "បន្ទាប់ពីការបញ្ជាទិញត្រូវបានដំណើរការលេខតាមដាននឹងផ្ញើទៅអ៊ីមែលដែលបានចុះឈ្មោះ។អ្នកអាចប្រើតំណនោះដើម្បីតាមដានបច្ចុប្បន្នភាពដឹកជញ្ជូន។",
      deliveryTimeQuestion: "តើពេលវេលាដឹកជញ្ជូនស្តង់ដារប៉ុន្មាន?",
      deliveryTimeAnswer: "ការដឹកជញ្ជូនស្តង់ដារជាទូទៅចំណាយពេល3ទៅ5ថ្ងៃធ្វើការ។ជម្រើសដឹកជញ្ជូនរហ័សមាននៅពេលបង់ប្រាក់ប្រសិនបើតំបន់របស់អ្នកគាំទ្រ។",
      packageDelayedQuestion: "តើមានអ្វីកើតឡើងប្រសិនបើកញ្ចប់របស់ខ្ញុំពន្យារពេល?",
      packageDelayedAnswer: "ប្រសិនបើក្រុមហ៊ុនដឹកជញ្ជូនរាយការណ៍ពីការពន្យារពេលស្ថានភាពចុងក្រោយនឹងបង្ហាញក្នុងតំណតាមដាន។ប្រសិនបើមិនមានចលនាយូរសូមទាក់ទងជំនួយជាមួយលេខបញ្ជាទិញ។",
      modifyOrderQuestion: "តើខ្ញុំអាចកែការបញ្ជាទិញបន្ទាប់ពីដាក់បានទេ?",
      modifyOrderAnswer: "ការកែប្រែអាចធ្វើបានក្នុងរយៈពេល2ម៉ោងបន្ទាប់ពីបង់ប្រាក់។បន្ទាប់ពីនោះការបញ្ជាទិញជាទូទៅចូលដំណើរការរៀបចំហើយមិនអាចកែបានទៀត។",
      qualityQuestion: "តើស្តង់ដារគុណភាពផលិតផលត្រូវបានផ្ទៀងផ្ទាត់ដូចម្តេច?",
      qualityAnswer: "ផលិតផលនីមួយៗត្រូវបានពិនិត្យមុនផ្ញើដើម្បីបញ្ជាក់ការវេចខ្ចប់ស្ថានភាពនិងស្តង់ដារគុណភាពមូលដ្ឋាន។",
      cancelQuestion: "តើខ្ញុំអាចបោះបង់ទំនិញមុនពេលផ្ញើបានទេ?",
      cancelAnswer: "ប្រសិនបើការបញ្ជាទិញមិនទាន់ចូលដំណាក់កាលរៀបចំឬប្រគល់ទៅក្រុមហ៊ុនដឹកជញ្ជូនក្រុមជំនួយជាទូទៅអាចជួយបោះបង់បាន។",
      warrantyQuestion: "តើខ្ញុំស្នើសុំជំនួយការធានាដូចម្តេច?",
      warrantyAnswer: "សូមជ្រើសរើសការបញ្ជាទិញដែលបានប្រគល់រួចនៅទំព័រទំនាក់ទំនង។ ប្រសិនបើផលិតផលមានការធានា ក្រុមជំនួយនឹងពិនិត្យក្នុងរយៈពេលធានា។ ផលិតផលគ្មានការធានាមានរយៈពេលជំនួយ 7 ថ្ងៃបន្ទាប់ពីប្រគល់។",
      declinedQuestion: "ហេតុអ្វីបានជាការទូទាត់របស់ខ្ញុំត្រូវបានបដិសេធ?",
      declinedAnswer: "ការបដិសេធអាចកើតឡើងដោយសារកំណត់កាតព័ត៌មានផ្ទៀងផ្ទាត់មិនត្រូវឬការត្រួតពិនិត្យសុវត្ថិភាពពីធនាគារ។សូមពិនិត្យព័ត៌មានវិក្កយបត្រហើយសាកល្បងម្តងទៀតឬប្រើវិធីបង់ប្រាក់ផ្សេង។",
    },
    categories: {
      all: "ទាំងអស់",
      milk: "ទឹកដោះគោ",
      clothing: "សម្លៀកបំពាក់",
      shoes: "ស្បែកជើង",
      feedingNursing: "ការបំបៅនិងថែទាំ",
      diaperingCare: "កន្ទបនិងការថែទាំ",
      furniture: "គ្រឿងសង្ហារឹម",
      travelGear: "សម្ភារៈធ្វើដំណើរ",
      bathSkin: "ងូតទឹកនិងថែស្បែក",
      toyPlay: "ប្រដាប់ក្មេងលេង និងការលេង",
    },
    "Welcome Back": "សូមស្វាគមន៍មកវិញ",
    "Sign in to continue shopping": "ចូលគណនីដើម្បីបន្តទិញទំនិញ",
    "Email Address": "អាសយដ្ឋានអ៊ីមែល",
    "Password": "ពាក្យសម្ងាត់",
    "Enter your password": "បញ្ចូលពាក្យសម្ងាត់",
    "Remember me": "ចងចាំខ្ញុំ",
    "Forgot password?": "ភ្លេចពាក្យសម្ងាត់?",
    "Identifying...": "កំពុងផ្ទៀងផ្ទាត់...",
    "Sign In": "ចូលគណនី",
    "OR": "ឬ",
    "Sign in with Google": "ចូលគណនីតាមGoogle",
    "Dont have an account?": "មិនទាន់មានគណនី?",
    "Create one now": "បង្កើតគណនីឥឡូវនេះ",
    "Secure SSL Encryption": "ការអ៊ិនគ្រីបSSLសុវត្ថិភាព",
  },
};

export const staticTextTranslations = {
  kh: {
    "Quick category access": "ចូលប្រភេទរហ័ស",
    "Quick Access": "ចូលរហ័ស",
    "Shop by Category": "ទិញតាមប្រភេទ",
    "Browse now": "មើលឥឡូវនេះ",
    "items": "មុខទំនិញ",
    "Curated For You": "បានជ្រើសសម្រាប់អ្នក",
    "Best Sellers & New Arrivals": "លក់ដាច់បំផុតនិងផលិតផលថ្មី",
    "Parent Reviews": "មតិយោបល់ពីឪពុកម្តាយ",
    "Trusted by families every day": "គ្រួសារជាច្រើនជឿទុកចិត្តរៀងរាល់ថ្ងៃ",
    "Limited Time Offer": "ការផ្តល់ជូនមានកំណត់",
    "Ends in 24h": "បញ្ចប់ក្នុង24ម៉ោង",
    "On Selected Items": "លើទំនិញដែលបានជ្រើស",
    "Secure Checkout": "បង់ប្រាក់ដោយសុវត្ថិភាព",
    "Subtotal": "សរុបរង",
    "Shipping": "ដឹកជញ្ជូន",
    "Free": "ឥតគិតថ្លៃ",
    "Tax (8.0%)": "ពន្ធ(8.0%)",
    "Tax (8%)": "ពន្ធ(8%)",
    "Total": "សរុប",
    "Proceed to Checkout": "បន្តទៅការទូទាត់",
    "Secure Payment": "ការទូទាត់មានសុវត្ថិភាព",
    "Order Summary": "សង្ខេបការបញ្ជាទិញ",
    "Checkout": "ទូទាត់",
    "Back to Cart": "ត្រឡប់ទៅកន្រ្តកទំនិញ",
    "Shipping Address": "អាសយដ្ឋានដឹកជញ្ជូន",
    "Payment Method": "វិធីបង់ប្រាក់",
    "Place Order": "ដាក់ការបញ្ជាទិញ",
    "Placing Order...": "កំពុងដាក់ការបញ្ជាទិញ...",
    "Full Name": "ឈ្មោះពេញ",
    "Enter your full name": "បញ្ចូលឈ្មោះពេញរបស់អ្នក",
    "Pin Location": "កំណត់ទីតាំង",
    "Location Selected": "បានជ្រើសទីតាំង",
    "Address": "អាសយដ្ឋាន",
    "Select a location on the map": "ជ្រើសទីតាំងលើផែនទី",
    "City / Province": "ទីក្រុង/ខេត្ត",
    "City / Province will appear here": "ទីក្រុង/ខេត្តនឹងបង្ហាញនៅទីនេះ",
    "Phone Number": "លេខទូរស័ព្ទ",
    "Cash on Delivery": "បង់ប្រាក់ពេលទទួលទំនិញ",
    "BAKONG_KHQR": "បាគងKHQR",
    "Qty": "ចំនួន",
    "Qty:": "ចំនួន:",
    "Size": "ទំហំ",
    "Size:": "ទំហំ:",
    "Choose Size": "ជ្រើសទំហំ",
    "Quantity": "ចំនួន",
    "Loading order...": "កំពុងផ្ទុកការបញ្ជាទិញ...",
    "Order Not Found": "រកមិនឃើញការបញ្ជាទិញ",
    "Back to orders": "ត្រឡប់ទៅការបញ្ជាទិញ",
    "Items": "ទំនិញ",
    "Order History": "ប្រវត្តិការបញ្ជាទិញ",
    "View details of your past orders": "មើលព័ត៌មានលម្អិតនៃការបញ្ជាទិញមុនៗរបស់អ្នក",
    "No Orders Found": "រកមិនឃើញការបញ្ជាទិញ",
    "You haven't placed any orders yet. Start shopping to find the best essentials for your baby.": "អ្នកមិនទាន់បានដាក់ការបញ្ជាទិញទេ។ចាប់ផ្តើមទិញទំនិញដើម្បីស្វែងរកសម្ភារៈល្អៗសម្រាប់កូនរបស់អ្នក។",
    "Order Placed": "បានដាក់ការបញ្ជាទិញ",
    "Orders Placed": "ការបញ្ជាទិញដែលបានដាក់",
    "Payment:": "ការទូទាត់:",
    "Pending": "កំពុងរង់ចាំ",
    "Processing": "កំពុងដំណើរការ",
    "Shipped": "បានផ្ញើ",
    "Cancelled": "បានបោះបង់",
    "Paid": "បានបង់ប្រាក់",
    "Failed": "បរាជ័យ",
    "Refunded": "បានសងប្រាក់",
    "Your wishlist is empty": "បញ្ជីទំនិញនៅទទេ",
    "Save your favorite items here to find them easily later.": "រក្សាទុកទំនិញដែលអ្នកចូលចិត្តនៅទីនេះដើម្បីរកឃើញងាយៗនៅពេលក្រោយ។",
    "Start exploring our collection today.": "ចាប់ផ្តើមមើលបណ្តុំផលិតផលរបស់យើងថ្ងៃនេះ។",
    "Saved Items": "ទំនិញដែលបានរក្សាទុក",
    "saved for later": "រក្សាទុកសម្រាប់ពេលក្រោយ",
    "My Wishlist": "បញ្ជីទំនិញរបស់ខ្ញុំ",
    "Price": "តម្លៃ",
    "Stock": "មានស្តុក",
    "Sold": "លក់អស់",
    "Sold Out": "អស់ស្តុក",
    "Continue Shopping": "បន្តទិញទំនិញ",
    "Added to Cart": "បានបន្ថែមទៅកន្រ្តកទំនិញ",
    "Action Failed": "សកម្មភាពបរាជ័យ",
    "Item Removed": "បានលុបទំនិញ",
    "Update Failed": "ធ្វើបច្ចុប្បន្នភាពបរាជ័យ",
    "Remove Failed": "លុបមិនបានសម្រេច",
    "Clear Failed": "សម្អាតមិនបានសម្រេច",
    "Login Required": "ត្រូវការចូលគណនី",
    "Please login to add items to your cart": "សូមចូលគណនីដើម្បីបន្ថែមទំនិញទៅក្នុងកន្រ្តកទំនិញ",
    "In Stock": "មានស្តុក",
    "Decrease": "បន្ថយ",
    "Increase": "បង្កើន",
    "Remove Item": "លុបទំនិញ",
    "Start Shopping": "ចាប់ផ្តើមទិញទំនិញ",
    "Order ID": "លេខបញ្ជាទិញ",
    "Please Sign In": "សូមចូលគណនី",
    "Something went wrong": "មានបញ្ហាកើតឡើង",
    "Error loading product": "មានបញ្ហាក្នុងការផ្ទុកផលិតផល",
    "Product not found": "រកមិនឃើញផលិតផល",
    "Go back": "ត្រឡប់ក្រោយ",
    "Back": "ត្រឡប់ក្រោយ",
    "Product Description": "ការពិពណ៌នាផលិតផល",
    "Add to Cart": "បន្ថែមទៅកន្រ្តកទំនិញ",
    "Log in to Buy": "ចូលគណនីដើម្បីទិញ",
    "Customer Reviews": "មតិយោបល់អតិថិជន",
    "Verified Reviews": "មតិយោបល់ដែលបានផ្ទៀងផ្ទាត់",
    "No Reviews Yet": "មិនទាន់មានមតិយោបល់",
    "Be the first to review this product.": "ធ្វើជាមនុស្សដំបូងដែលផ្តល់មតិយោបល់លើផលិតផលនេះ។",
    "Please log in": "សូមចូលគណនី",
    "to write a review": "ដើម្បីសរសេរមតិយោបល់",
    "Log In": "ចូលគណនី",
    "Write a Review": "សរសេរមតិយោបល់",
    "Review Submitted": "បានផ្ញើមតិយោបល់",
    "Thank you for your feedback!": "អរគុណសម្រាប់មតិយោបល់របស់អ្នក!",
    "Rating": "ការវាយតម្លៃ",
    "Your Review": "មតិយោបល់របស់អ្នក",
    "Write your review here...": "សរសេរមតិយោបល់របស់អ្នកនៅទីនេះ...",
    "Submit Review": "ផ្ញើមតិយោបល់",
    "Submitting...": "កំពុងផ្ញើ...",
    "Excellent": "ល្អឥតខ្ចោះ",
    "Very Good": "ល្អណាស់",
    "Good": "ល្អ",
    "Poor": "ខ្សោយ",
    "Bad": "អាក្រក់",
    "Generating your secure QR code…": "កំពុងបង្កើត QR កូដសុវត្ថិភាពរបស់អ្នក...",
    "Payment Error": "កំហុសការទូទាត់",
    "Payment Successful!": "ការទូទាត់ជោគជ័យ!",
    "Your payment has been confirmed. Redirecting to your order…": "ការទូទាត់របស់អ្នកត្រូវបានបញ្ជាក់។កំពុងបញ្ជូនទៅការបញ្ជាទិញរបស់អ្នក...",
    "Payment Failed": "ការទូទាត់បរាជ័យ",
    "Something went wrong. Please try again or choose a different payment method.": "មានបញ្ហាកើតឡើង។សូមសាកល្បងម្តងទៀតឬជ្រើសរើសវិធីបង់ប្រាក់ផ្សេង។",
    "QR Code Expired": "QR កូដបានផុតកំណត់",
    "Your QR code has expired after 5 minutes. Generate a new one to complete your payment.": "QR កូដរបស់អ្នកបានផុតកំណត់បន្ទាប់ពី 5 នាទី។ សូមបង្កើត QR កូដថ្មីដើម្បីបញ្ចប់ការទូទាត់។",
    "Generating…": "កំពុងបង្កើត...",
    "New QR Code": "QR កូដថ្មី",
    "Scan to Pay": "ស្កេនដើម្បីបង់ប្រាក់",
    "Use any KHQR-compatible banking app to complete your payment": "ប្រើកម្មវិធីធនាគារដែលគាំទ្រKHQRដើម្បីបញ្ចប់ការទូទាត់",
    "Expires In 5 Minutes": "ផុតកំណត់ក្នុងរយៈពេល 5 នាទី",
    "Amount Due": "ចំនួនត្រូវបង់",
    "How to Pay": "របៀបបង់ប្រាក់",
    "Checking payment status automatically": "កំពុងពិនិត្យស្ថានភាពការទូទាត់ដោយស្វ័យប្រវត្តិ",
    "Accepted by ABA, ACLEDA, Canadia, Chip Mong, and all KHQR-enabled banks": "ទទួលដោយABA, ACLEDA, Canadia, Chip Mongនិងធនាគារដែលគាំទ្រKHQRទាំងអស់",
    "Track Order": "តាមដានការបញ្ជាទិញ",
    "Enter your order number to check the current status and delivery progress": "បញ្ចូលលេខបញ្ជាទិញរបស់អ្នកដើម្បីពិនិត្យស្ថានភាពបច្ចុប្បន្ននិងដំណើរការដឹកជញ្ជូន",
    "Enter order number (e.g. ORD-2026-001)": "បញ្ចូលលេខបញ្ជាទិញ(ឧ. ORD-2026-001)",
    "Order": "ការបញ្ជាទិញ",
    "Status": "ស្ថានភាព",
    "Estimated Delivery": "ពេលដឹកជញ្ជូនប៉ាន់ស្មាន",
    "Carrier": "ក្រុមហ៊ុនដឹកជញ្ជូន",
    "Tracking History": "ប្រវត្តិតាមដាន",
    "Origin": "ចំណុចចាប់ផ្តើម",
    "Destination": "គោលដៅ",
    "Recipient": "អ្នកទទួល",
    "Tracking ID": "លេខតាមដាន",
    "Delivered": "បានដឹកជញ្ជូន",
    "Please enter your credentials to view history.": "សូមចូលគណនីដើម្បីមើលប្រវត្តិ។",
    "My Orders": "ការបញ្ជាទិញរបស់ខ្ញុំ",
    "Shipping to:": "ដឹកជញ្ជូនទៅ:",
    "Please login to manage your addresses.": "សូមចូលគណនីដើម្បីគ្រប់គ្រងអាសយដ្ឋានរបស់អ្នក។",
    "Address Book": "សៀវភៅអាសយដ្ឋាន",
    "Label (e.g. Home)": "ស្លាក(ឧ.ផ្ទះ)",
    "Full name": "ឈ្មោះពេញ",
    "Phone": "ទូរស័ព្ទ",
    "City": "ទីក្រុង",
    "Postal code": "លេខកូដប្រៃសណីយ៍",
    "Country": "ប្រទេស",
    "Address line 1": "អាសយដ្ឋានជួរទី1",
    "Address line 2 (optional)": "អាសយដ្ឋានជួរទី2 (ស្រេចចិត្ត)",
    "Saved Addresses": "អាសយដ្ឋានដែលបានរក្សាទុក",
    "Loading addresses...": "កំពុងផ្ទុកអាសយដ្ឋាន...",
    "No saved addresses yet.": "មិនទាន់មានអាសយដ្ឋានដែលបានរក្សាទុក។",
    "Edit": "កែប្រែ",
    "Delete": "លុប",
    "Settings": "ការកំណត់",
    "Manage your account preferences": "គ្រប់គ្រងចំណូលចិត្តគណនីរបស់អ្នក",
    "Profile Settings": "ការកំណត់ប្រវត្តិរូប",
    "Enter your name": "បញ្ចូលឈ្មោះរបស់អ្នក",
    "Enter email": "បញ្ចូលអ៊ីមែល",
    "Notifications": "ការជូនដំណឹង",
    "Appearance": "រូបរាង",
    "Language": "ភាសា",
    "English": "អង់គ្លេស",
    "Khmer": "ខ្មែរ",
    "Dark Mode": "របៀបងងឹត",
    "Security & Privacy": "សុវត្ថិភាពនិងឯកជនភាព",
    "Delete Account?": "លុបគណនី?",
    "Signed in with Google": "បានចូលគណនីតាមGoogle",
    "We'll send a 6-digit code to your email to verify this request.": "យើងនឹងផ្ញើលេខកូដ6ខ្ទង់ទៅអ៊ីមែលរបស់អ្នកដើម្បីផ្ទៀងផ្ទាត់សំណើនេះ។",
    "Cancel": "បោះបង់",
    "Send Code": "ផ្ញើកូដ",
    "Confirmation Code": "លេខកូដបញ្ជាក់",
    "Confirm Delete": "បញ្ជាក់ការលុប",
    "Confirm Identity": "បញ្ជាក់អត្តសញ្ញាណ",
    "Enter your password to confirm account deletion.": "បញ្ចូលពាក្យសម្ងាត់របស់អ្នកដើម្បីបញ្ជាក់ការលុបគណនី។",
    "Enter your current password": "បញ្ចូលពាក្យសម្ងាត់បច្ចុប្បន្ន",
    "Access Denied": "មិនអនុញ្ញាតឱ្យចូល",
    "Please log in to view your account overview and manage your profile.": "សូមចូលគណនីដើម្បីមើលទិដ្ឋភាពទូទៅនៃគណនីនិងគ្រប់គ្រងប្រវត្តិរូប។",
    "Change": "ប្តូរ",
    "Success": "ជោគជ័យ",
    "Error": "កំហុស",
    "View All": "មើលទាំងអស់",
    "No orders yet": "មិនទាន់មានការបញ្ជាទិញ",
    "When you make a purchase, it will appear here.": "នៅពេលអ្នកទិញវានឹងបង្ហាញនៅទីនេះ។",
    "Update Profile": "ធ្វើបច្ចុប្បន្នភាពប្រវត្តិរូប",
    "Manage your personal information and contact details.": "គ្រប់គ្រងព័ត៌មានផ្ទាល់ខ្លួននិងព័ត៌មានទំនាក់ទំនង។",
    "Email Address": "អាសយដ្ឋានអ៊ីមែល",
    "Security Settings": "ការកំណត់សុវត្ថិភាព",
    "Update your password to keep your account secure.": "ធ្វើបច្ចុប្បន្នភាពពាក្យសម្ងាត់ដើម្បីរក្សាគណនីឱ្យមានសុវត្ថិភាព។",
    "Current Password": "ពាក្យសម្ងាត់បច្ចុប្បន្ន",
    "New Password": "ពាក្យសម្ងាត់ថ្មី",
    "Confirm New Password": "បញ្ជាក់ពាក្យសម្ងាត់ថ្មី",
    "Enter current password": "បញ្ចូលពាក្យសម្ងាត់បច្ចុប្បន្ន",
    "Enter new password": "បញ្ចូលពាក្យសម្ងាត់ថ្មី",
    "Confirm new password": "បញ្ជាក់ពាក្យសម្ងាត់ថ្មី",
    "John Doe": "John Doe",
    "john@example.com": "john@example.com",
    "Welcome Back": "សូមស្វាគមន៍មកវិញ",
    "Sign in to continue shopping": "ចូលគណនីដើម្បីបន្តទិញទំនិញ",
    "Password": "ពាក្យសម្ងាត់",
    "Remember me": "ចងចាំខ្ញុំ",
    "Forgot password?": "ភ្លេចពាក្យសម្ងាត់?",
    "Identifying...": "កំពុងផ្ទៀងផ្ទាត់...",
    "Sign In": "ចូលគណនី",
    "OR": "ឬ",
    "Sign in with Google": "ចូលគណនីតាមGoogle",
    "Dont have an account?": "មិនទាន់មានគណនី?",
    "Create one now": "បង្កើតគណនីឥឡូវនេះ",
    "Sign up with Google": "ចុះឈ្មោះតាមGoogle",
    "Secure SSL Encryption": "ការអ៊ិនគ្រីបSSLសុវត្ថិភាព",
    "Secure Registration • SSL Encrypted": "ការចុះឈ្មោះមានសុវត្ថិភាព•បានអ៊ិនគ្រីបSSL",
    "Min. 6 characters": "យ៉ាងតិច6តួអក្សរ",
    "Create a strong password": "បង្កើតពាក្យសម្ងាត់រឹងមាំ",
    "Enter your password": "បញ្ចូលពាក្យសម្ងាត់",
    "Enter your email address": "បញ្ចូលអាសយដ្ឋានអ៊ីមែល",
    "Redirecting you to home page...": "កំពុងបញ្ជូនអ្នកទៅទំព័រដើម...",
    "About Us": "អំពីយើង",
    "Redefining the online shopping experience since 2019": "កំណត់បទពិសោធន៍ទិញទំនិញអនឡាញឡើងវិញតាំងពីឆ្នាំ2019",
    "Our Mission": "បេសកកម្មរបស់យើង",
    "Curated Quality": "គុណភាពដែលបានជ្រើសរើស",
    "Our Vision": "ចក្ខុវិស័យរបស់យើង",
    "Modern Essentials": "សម្ភារៈចាំបាច់សម័យថ្មី",
    "Years of Growth": "ឆ្នាំនៃការរីកចម្រើន",
    "Our Advantages": "អត្ថប្រយោជន៍របស់យើង",
    "Why Choose Us": "ហេតុអ្វីជ្រើសរើសយើង",
    "Our Journey": "ដំណើររបស់យើង",
    "Milestones": "សមិទ្ធផលសំខាន់ៗ",
    "Leadership": "ភាពជាអ្នកដឹកនាំ",
    "Our Team": "ក្រុមរបស់យើង",
    "Privacy Policy": "គោលការណ៍ឯកជនភាព",
    "How we collect, use, and protect your data": "របៀបដែលយើងប្រមូលប្រើនិងការពារទិន្នន័យរបស់អ្នក",
    "Personal Data": "ទិន្នន័យផ្ទាល់ខ្លួន",
    "Technical Data": "ទិន្នន័យបច្ចេកទេស",
    "Security Measures": "វិធានការសុវត្ថិភាព",
    "Retention Policy": "គោលការណ៍រក្សាទុក",
    "Contact Us": "ទាក់ទងយើង",
    "Terms of Service": "លក្ខខណ្ឌសេវាកម្ម",
    "The rules and guidelines governing your use of our platform": "ច្បាប់និងគោលការណ៍ណែនាំសម្រាប់ការប្រើប្រាស់វេទិការបស់យើង",
    "Product Information": "ព័ត៌មានផលិតផល",
    "Pricing & Availability": "តម្លៃនិងភាពមានស្តុក",
    "Account Responsibilities": "ទំនួលខុសត្រូវគណនី",
    "Termination": "ការបញ្ចប់",
    "Order Confirmation": "ការបញ្ជាក់ការបញ្ជាទិញ",
    "Payment Terms": "លក្ខខណ្ឌការទូទាត់",
    "Delivery": "ការដឹកជញ្ជូន",
    "Warranty Support": "ជំនួយការធានា",
    "Liability Cap": "ដែនកំណត់ទំនួលខុសត្រូវ",
    "Acceptance of Terms": "ការយល់ព្រមលើលក្ខខណ្ឌ",
    "By accessing and using our platform, you accept and agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree to these terms, please do not use our services.": "តាមរយៈការចូលប្រើប្រាស់ និងប្រើប្រាស់វេទិការបស់យើង អ្នកយល់ព្រម និងឯកភាពចំពោះការភ្ជាប់ខ្លួនទៅនឹងលក្ខខណ្ឌសេវាកម្មទាំងនេះ និងគោលការណ៍ឯកជនភាពរបស់យើង។ ប្រសិនបើអ្នកមិនយល់ព្រមនឹងលក្ខខណ្ឌទាំងនេះទេ សូមកុំប្រើប្រាស់សេវាកម្មរបស់យើង។",
    "Products & Services": "ផលិតផល និងសេវាកម្ម",
    "We strive for accuracy in product descriptions, pricing, and availability. However, we reserve the right to correct any errors or omissions and to update information at any time.": "យើងខិតខំប្រឹងប្រែងដើម្បីភាពត្រឹមត្រូវក្នុងការពិពណ៌នាផលិតផល តម្លៃ និងភាពអាចរកបាន។ ទោះជាយ៉ាងណាក៏ដោយ យើងរក្សាសិទ្ធិក្នុងការកែតម្រូវរាល់កំហុសឆ្ងង ឬការខកខាននានា និងធ្វើបច្ចុប្បន្នភាពព័ត៌មាននៅពេលណាក៏បាន។",
    "Prices are subject to change without notice. We reserve the right to modify or discontinue any product without liability at any time.": "តម្លៃអាចផ្លាស់ប្តូរដោយគ្មានការជូនដំណឹងជាមុន។ យើងរក្សាសិទ្ធិក្នុងការកែប្រែ ឬបញ្ឈប់ផលិតផលណាមួយដោយគ្មានការទទួលខុសត្រូវនៅពេលណាក៏បាន។",
    "User Accounts": "គណនីអ្នកប្រើប្រាស់",
    "Provide accurate and complete information": "ផ្តល់ព័ត៌មានត្រឹមត្រូវ និងពេញលេញ",
    "Maintain the confidentiality of your password": "រក្សាការសម្ងាត់នៃពាក្យសម្ងាត់របស់អ្នក",
    "You are responsible for all activities under your account": "អ្នកត្រូវទទួលខុសត្រូវចំពោះរាល់សកម្មភាពទាំងអស់នៅក្រោមគណនីរបស់អ្នក",
    "Notify us immediately of any security breach": "ជូនដំណឹងដល់យើងជាបន្ទាន់អំពីការរំលោភលើសុវត្ថិភាពណាមួយ",
    "We reserve the right to suspend or terminate your account at any time for violation of these terms or unauthorized activity, without prior notice.": "យើងរក្សាសិទ្ធិក្នុងការផ្អាក ឬលុបគណនីរបស់អ្នកនៅពេលណាក៏បាន ចំពោះការរំលោភលើលក្ខខណ្ឌទាំងនេះ ឬសកម្មភាពដែលគ្មានការអនុញ្ញាត ដោយគ្មានការជូនដំណឹងជាមុន។",
    "Orders & Payment": "ការបញ្ជាទិញ និងការទូទាត់",
    "Receipt of an order confirmation does not constitute acceptance. We reserve the right to cancel or refuse any order at our discretion.": "ការទទួលបានការបញ្ជាក់ការបញ្ជាទិញមិនមែនជាការយល់ព្រមទទួលយកនោះទេ។ យើងរក្សាសិទ្ធិក្នុងការលុបចោល ឬបដិសេធការបញ្ជាទិញណាមួយតាមការសម្រេចចិត្តរបស់យើង។",
    "All payments are processed securely. Prices are listed in the applicable currency and include applicable taxes. Payment is due at the time of purchase.": "រាល់ការទូទាត់ទាំងអស់ត្រូវបានដំណើរការដោយសុវត្ថិភាព។ តម្លៃត្រូវបានរាយបញ្ជីជាលុយដែលត្រូវអនុវត្ត និងរួមបញ្ចូលពន្ធដែលអាចអនុវត្តបាន។ ការទូទាត់ត្រូវធ្វើឡើងនៅពេលទិញ។",
    "Shipping & Warranty Support": "ការដឹកជញ្ជូន និងជំនួយការធានា",
    "Standard delivery typically takes 3-5 business days. Express options may be available. We are not responsible for delays caused by the carrier or unforeseen circumstances.": "ការដឹកជញ្ជូនស្តង់ដារជាធម្មតាចំណាយពេល 3-5 ថ្ងៃធ្វើការ។ ជម្រើសរហ័សអាចនឹងមាន។ យើងមិនទទួលខុសត្រូវចំពោះការពន្យារពេលដែលបង្កឡើងដោយក្រុមហ៊ុនដឹកជញ្ជូន ឬស្ថានភាពដែលមើលមិនឃើញជាមុននោះឡើយ។",
    "Delivered orders can be reviewed within the product warranty period when one is available. Products without warranty coverage use a 7-day support window after delivery for product issues.": "ការបញ្ជាទិញដែលបានប្រគល់រួចអាចត្រូវបានពិនិត្យក្នុងរយៈពេលធានាផលិតផល ប្រសិនបើមាន។ ផលិតផលគ្មានការធានាមានរយៈពេលជំនួយ 7 ថ្ងៃបន្ទាប់ពីប្រគល់សម្រាប់បញ្ហាផលិតផល។",
    "Intellectual Property": "កម្មសិទ្ធិបញ្ញា",
    "All content on this platform — including text, images, logos, and designs — is the property of our company and is protected by applicable intellectual property laws. Unauthorized use, reproduction, or distribution is strictly prohibited.": "រាល់ខ្លឹមសារទាំងអស់នៅលើវេទិកានេះ រួមទាំងអត្ថបទ រូបភាព ឡូហ្គោ និងការរចនា គឺជាកម្មសិទ្ធិរបស់ក្រុមហ៊ុនយើងខ្ញុំ ហើយត្រូវបានការពារដោយច្បាប់កម្មសិទ្ធិបញ្ញាដែលអាចអនុវត្តបាន។ ការប្រើប្រាស់ ការថតចម្លង ឬការចែកចាយដោយគ្មានការអនុញ្ញាតត្រូវបានហាមឃាត់ដាច់ខាត។",
    "Limitation of Liability": "ដែនកំណត់នៃការទទួលខុសត្រូវ",
    "Our liability is limited to the maximum extent permitted by law. We are not liable for indirect, incidental, or consequential damages arising from your use of our services. In any case, our total liability is capped at the purchase price of the product in question.": "ការទទួលខុសត្រូវរបស់យើងត្រូវបានកំណត់ចំពោះកម្រិតអតិបរមាដែលច្បាប់អនុញ្ញាត។ យើងមិនទទួលខុសត្រូវចំពោះការខូចខាតដោយប្រយោល ចៃដន្យ ឬជាផលវិបាកដែលកើតចេញពីការប្រើប្រាស់សេវាកម្មរបស់យើងនោះឡើយ។ ក្នុងករណីណាក៏ដោយ ការទទួលខុសត្រូវសរុបរបស់យើងត្រូវបានកំណត់ត្រឹមតម្លៃទិញផលិតផលដែលមានបញ្ហានោះ។",
    "For questions about these terms, please reach out to us:": "សម្រាប់សំណួរអំពីលក្ខខណ្ឌទាំងនេះ សូមទាក់ទងមកយើងខ្ញុំ៖",
    "Version": "កំណែ",
    "Last Updated": "ធ្វើបច្ចុប្បន្នភាពចុងក្រោយ"
  },
};

export const supportedLanguages = [
  { code: "en", labelKey: "language.english" },
  { code: "kh", labelKey: "language.khmer" },
];
